/**
 * CommonsSettlementsModule — renders the deterministic Commons settlements (G-track).
 *
 * Settlements are seed-derived content (Approach A): `settlementSites(seed)` is the
 * single source of truth, mirrored server-side. This module turns each NEARBY site
 * into geometry — an engine `CityPlan` (roads/parcels/building pads/street props)
 * with the biome-picked Higgsfield GLBs swapped onto the pads — instanced into ONE
 * draw call per prefab via `StaticInstancer`, streamed by player distance, with the
 * cooked trimesh collider proxies (bbox-box fallback) so collision matches the
 * visible walls. NPC positions stay server-authoritative
 * (the `versona` rows seeded by `seedCommonsSettlements`); this module only renders.
 *
 * Visual fidelity is confirmed in a real browser — this wires deterministic data to
 * the engine, which GPU-renders it.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';

import { Phase, makeSystem } from '../engine-compat.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { terrainSeat } from '../lib/terrain_seat.js';
import { terrainHeight } from '../../../shared/terrain.js';
import { settlementSites, SETTLEMENT_FOOTPRINT, type SettlementSite, type SettlementTier } from '@shared/settlements.js';
import { pickStructureForFunction, pickLandmark, civilizationOf, preferTower, STREETLIGHT_KEYS, ENTERABLE_CIV_KEYS, civDisplayName } from '../lib/biome_settlements.js';
import { planStreetFurniture } from '../lib/street_furniture.js';
import { buildRoadGroup, type RoadMaterialSet } from '../lib/road_mesh.js';
import { normalFromAlbedo } from '../lib/normal_from_albedo.js';
import { primeSettlementPlan, targetHeightFor, civGapFor, planFileFor } from '../lib/settlement_plan.js';
import { corridorHeightCap } from '../lib/districts.js';
import { buildSettlementDressing, dressingNightMats, type BuildingAnchor, type PrefabPlacement } from '../lib/settlement_dressing.js';
import { buildHinterlandDressing } from '../lib/hinterland_dressing.js';
import { setStructureSeat, clearStructureSeat } from '../lib/structure_seat.js';
import { uNightFactor } from './SkyModule.js';
import { SHIP_ALT_M } from '../lib/skyship_interior.js';
import { registerSkyShip, unregisterSkyShip, hullCenter, SKY_SHIP_PREFAB } from '../lib/skyship_registry.js';
import type { CityPlan } from 'enginex/city/config.js';
import { registerLamps, unregisterLamps, type LampAnchor } from './StreetLampsModule.js';
import { spaceportSpot, shuttleSpot, terminalSpot } from '../lib/ports.js';
import { interiorEligible, enterables, type EnterableBuilding } from '../lib/building_interiors.js';
import { registerBuildingStamps, buildingStampsNear, type BuildingStamp } from '../lib/terrain_stamp.js';
import { addStructureSolid, clearStructureSolids } from '../lib/structure_solids.js';
import { StaticInstancer, collectMeshParts } from '../lib/instancing.js';
import { queueTextureWarm, schedulePipelineWarm, warmThenShow } from '../lib/pipeline_warm.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { DbConnection } from '../module_bindings/index.js';
import { ASSET_STORE_BASE } from '../config.js';
import type { ColliderProxy } from '../lib/collider_proxy.js';
import { activePreset, currentDrawDistance } from '../lib/quality_tiers.js';
import { settlementBands, effectiveDistance, closingSpeed, BUILD_RADIUS, screenSize, screenLodFor, screenLodBias, loadPriority, refineTextureCap, LOOKAHEAD_S, swapBudget, dwellOk, SWAP_STARVE_MS, type SettlementBands } from '../lib/settlement_lod.js';
import { samplePlayerMotion, playerVelocity } from '../lib/player_motion.js';
import { SpatialHash2D, colliderRingWant, colliderRingWantFor, settleStagesFromMarks, settleReports, type SettleReport, type Placed } from '../lib/settle_hotspots.js';
import { warmPending, warmResources } from '../lib/pipeline_warm.js';
import { FADE_MS, fadePair } from '../lib/lod_fade.js';
import { propLod, isPropKey } from '../lib/near_lod.js';
import { shapeTextureCap, skinWantedAt, skinBandFor, skinsToEvict, SKIN_LOADS_INFLIGHT, SKIN_MAX_RESIDENT, SKIN_PREFETCH_LOOKAHEADS } from '../lib/lod0_skins.js';
import { lod0Ledger, type LoadMetrics } from '../lib/lod0_ledger.js';
import { viewConeWeight, occludedByPlan, footprintRadius, OCCLUDED_DEMOTE_MS, OCCLUSION_MAX_DIST_M, type Occluder } from '../lib/lod_visibility.js';
/** Occlusion draw culling: hide a building's draws after this long behind the plan occluders. */
const OCCLUSION_HIDE_MS = 400;
import { collectResources } from '../lib/pipeline_warm.js';
import { yieldToMain } from '../lib/yield_main.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';

/** Distance (m) from the player at which a settlement streams in (by tier).
 *  Must comfortably exceed the tier's PLAN radius (city plans now reach ~420 m
 *  — streaming in at 350 m meant you were standing IN the city before it
 *  appeared). ×10 wave: 320–460 m towers must appear before they'd dominate
 *  the horizon, so the radii grew with the skyline (far assemblies use LOD1). */
// Build-in radius per tier at scale 1 — the ladder (lib/settlement_lod.ts)
// widens it to ¾ of the draw distance and moves every threshold out with
// the player's closing speed (fast-approach wave 2026-09-02).
const RENDER_RADIUS: Record<SettlementTier, number> = BUILD_RADIUS;
// Headless-probe FAST mode ('atlas:probe-fast', distinct from the lighting/
// telemetry flag 'atlas:probe'): burst the assembly budget — a SwiftShader
// probe runs ~1-2 fps, so the per-frame time-slice that protects a 60 fps
// player stretches a city assembly to tens of minutes there. Perf-measurement
// probes must NOT set this — it changes the very streaming path they measure.
const PROBE_MODE = ((): boolean => {
  try { return localStorage.getItem('atlas:probe-fast') === '1'; } catch { return false; }
})();
const HYSTERESIS = 40;
const TICK_EVERY = 12;      // re-evaluate streaming ~5×/s (60fps) — not every frame
/** Max street props rendered per settlement — CityGenerator emits 2000+ for a
 *  metro-radius city; subsample evenly down to a tidy count by tier. */
// Streetscape quotas (research pass 2026-07-22): lamps carry the night
// silhouette at ~25 m rhythm (engine spacing preserved by type-aware thinning,
// never a type-blind stride); benches belong where people gather (Whyte).
// (Per-category prop caps moved into street_furniture.ts's planner budgets.)
/** Settlement GLBs normalize to ~1.9 m and are registered at defaultScale 1 (correct for
 *  avatars, toy-sized for buildings), so we IGNORE defaultScale and size geometry to fit:
 *  buildings to their CityGenerator pad footprint (real metres, measured against the GLB
 *  bbox so it's robust to the exact normalization); props/landmarks use fixed scales. */
const BUILD_MIN_SCALE = 5;
const BUILD_MAX_SCALE = 26;
const BUILD_FILL = 0.88;    // buildings claim ~88% of their pad — near-continuous frontage (enclosure)
const MIN_PAD_M = 6;        // skip pads smaller than this — tiny GLBs that'd overflow/clutter
const LANDMARK_SCALE = 8;   // monuments/fountains → ~15 m
const PROP_SCALE = 2.5;     // lamps/benches → ~4–5 m
const LABEL_HEIGHT_M = 4;   // world height of a debug GLB-name label
/** Per-prefab size corrections (× the fitted scale) — some GLBs normalize smaller than
 *  the rest and need a boost to read at the right size. Hand-tuned in-browser. */
const BUILDING_SCALE_MULT: Record<string, number> = {
  'structure:hab_pod_medium': 10,
  'structure:observatory': 8,
  'structure:academy': 8,
  'structure:admin_tower': 8,
  'structure:villa_luxury': 8,
  'structure:apartment_spire_small': 8,
  'structure:comms_tower': 8,
  'structure:courtyard_home': 4,
  'structure:modular_duplex': 3,
  'structure:hab_dome_family': 3,
  // Spec-3 archetype prefabs (first-pass sizes — final tuning is an in-browser pass).
  'structure:spaceport_terminal': 8, 'structure:transit_hub': 6, 'structure:market_hall': 6,
  'structure:council_chamber': 8, 'structure:apartment_spire_tall': 8, 'structure:landing_pad_structure': 6,
  'structure:fusion_station': 6, 'structure:hangar': 6, 'structure:water_reclamation': 5,
  'structure:medical_bay': 5, 'structure:hydroponics_dome': 5, 'structure:greenhouse_public': 5,
  'structure:warehouse': 5, 'structure:workshop_garage': 4, 'structure:security_post': 4,
  'structure:bunker_hab': 4, 'structure:cliff_hab': 4, 'structure:stilt_hab': 4, 'structure:frontier_shack': 4,
  'structure:terrace_row_home': 4, 'structure:lounge_cantina': 4, 'structure:cafe_pavilion': 3,
  'structure:market_kiosk_cluster': 3, 'structure:container_prefab_home': 3, 'structure:hab_pod_small': 6,
  'structure:observatory_home': 5, 'structure:greenhouse_home': 4,
};
// Interior-eligibility diagnostic tallies (window.__intDiag — probe/debug only):
// per building key, how many placements were checked vs accepted + last size.
const intDiag: Record<string, { n: number; ok: number; w: number; h: number; d: number }> = {};
(globalThis as unknown as Record<string, unknown>).__intDiag = intDiag;

const BUILD_SCALE_CAP = 180;  // absolute scale ceiling (a multiplied building can get large)
const BUILD_GAP_M = 10;       // min clear gap between LEGACY building footprints (short-block packing)

// ── Civilization-pack scaling (2026-07-20 layout overhaul) ───────────────────
// The civ GLBs (structure:civ*) are cooked in REAL METERS (~60 m towers), so
// the legacy fit-scale (min clamp 5×) blew them into 300 m monsters. They get
// their own path: a radial HEIGHT-BAND target (the Gaussian-falloff skyline —
// tall core, mid ring, low rim; the standard procedural-city density device)
// with the scale derived from the model's measured height, softly capped by
// the pad so a tower never swallows its street.
const CIV_PREFIX = 'structure:civ';

// ── The living spaceport ship cycle (shared wall-clock — every client sees the
// same phase, like the weather fronts). Grounded 4 min → 45 s climb into
// space → 10 min away → 45 s descent BACK ONTO THE EXACT PAD SPOT.
const SHIP_GROUNDED_S = 240;
const SHIP_ASCENT_S = 45;
const SHIP_AWAY_S = 600;
const SHIP_DESCENT_S = 45;
const SHIP_CYCLE_S = SHIP_GROUNDED_S + SHIP_ASCENT_S + SHIP_AWAY_S + SHIP_DESCENT_S;
/** Altitude at which the ship counts as "in space" and despawns visually. */
const SHIP_CEILING_M = 3200;
const CIV_SCALE_MIN = 0.1;
/** ~460 m supertall / 60 m model = 7.7 — ceiling 8 (monumental ×10 wave). */
const CIV_SCALE_MAX = 8;
/** Footprint budget per tier = the street LATTICE pitch (city local roads run
 *  ~46 m apart, towns ~26, hamlets ~18): a building may fill its whole block
 *  wall-to-wall (real supertall districts do), but never swallow the street.
 *  This REPLACES the old pad-fit soft cap (fit × 2.5), which crushed every
 *  wide-footprint model to toy scale (a 90 m-long complex on a 14 m pad
 *  rendered 4 m tall — the "structures are tiny" report). */
const CIV_LOT_W: Record<SettlementTier, number> = { city: 60, town: 34, hamlet: 20, capital: 160, district: 60, circuit: 34 };
/** Landmark presence per tier (m) for civ landmark models (×10 wave). */
const CIV_LANDMARK_H: Record<SettlementTier, number> = { city: 340, town: 250, hamlet: 90, capital: 520, district: 340, circuit: 120 };
/**
 * Landmark FOOTPRINT cap per tier (m, the larger of width/depth after
 * scaling). The height rule alone scaled a 9 m tall civ-A "leisure
 * structure" ×38 into a 1.9 × 3.4 km slab across its city (and the 22–34 m
 * tall outpost/survey/storage halls ×10–16 into km-long hulls) — the "one
 * massive structure whose scale is too big" report, 2026-09-02. Mirrored in
 * lib/settlement_proxy_bake.ts (the far tier bakes the same scale).
 */
const CIV_LANDMARK_LOT_W: Record<SettlementTier, number> = { city: 160, town: 100, hamlet: 50, capital: 220, district: 160, circuit: 100 };
// Item 2 (2026-07-20): far settlements assemble from cooked LOD-1 variants —
// full-detail towers measured ~166M tris at settlement-city (verify:perf).
// The LOD0 radius, the build radius and the warm ring now come from ONE
// ladder (lib/settlement_lod.ts, `settlementBands`) evaluated on the
// EFFECTIVE distance (distance − closing speed × the build-time budget), so
// a hovercraft at cruise crosses every threshold ~15 s earlier than on foot
// and arrives at a built city instead of the far-tier ring. LOD stays
// STICKY + UPGRADE-ONLY (see tick()): a built LOD1 city only ever upgrades to
// LOD0 once inside the detail band, never downgrades mid-flight.

/** R8 (screen-space LOD): one placed civ building — enough to recompose its matrix at any level. */
interface BuildingLodRec {
  key: string; assetId: bigint; kind: 'building' | 'landmark';
  /** compose inputs (already pivot-compensated origin) */
  px: number; py: number; pz: number; yaw: number; scale: number;
  /** pad position + scaled height (the screen-size measure) */
  cx: number; cz: number; h: number;
  lod: 0 | 1 | 2 | 3;
  /** CURRENT instances + parts — the SAME arrays the enterable hide/restore closures hold (mutated in place on a swap). */
  gids: number[]; parts: Array<{ geometry: THREE.BufferGeometry; material: THREE.Material; matrix: THREE.Matrix4 }>;
  hidden: boolean; swapping: boolean;
  /** S7: performance.now() of the last swap (0 = never) — the dwell keeps a building from oscillating at a hysteresis edge. */
  lastSwapAt: number;
  /** L9: when the building first became a candidate for its current target (0 = not a candidate). */
  candSince: number;
  /** L4: inscribed footprint radius (m) and when the building was first seen occluded (0 = visible). */
  fr: number; occludedSince: number; idx: number;
  /** Occlusion draw culling: instances faded out while the plan occluders hide the building. */
  drawHidden?: boolean;
}
interface LodMaster { object: THREE.Object3D; release: () => void; users: number; loading?: Promise<LodMaster | null>; /** when users last dropped to 0 (prefetched or retired levels are released after a grace) */ idleSince?: number; /** L9: load timing + fetch/parse metrics of this master. */ loadStartedAt?: number; loadedAt?: number; metrics?: LoadMetrics }
/** Every colliding placement (civ buildings, terminals, pads, solid furniture): the in-place hull upgrade re-adds under `cid`. */
interface SiteCollider {
  key: string; cid: string; px: number; py: number; pz: number; yaw: number; scale: number;
  /** The building's FOOTPRINT center (the plan's pad, pl.wx/wz) — the ring
   *  measures walking range to THIS. px/pz is the pivot-compensated body
   *  origin, which for the off-pivot civ GLBs sits 40–1290 m from the
   *  building (the PIVOT LAW): measuring the ring against it dropped every
   *  civ tower's collider the tick after assembly added it, and never
   *  re-added it — "structures have no collision when walking, but a vehicle
   *  collides" (field report 2026-09-03; the vehicle registry keys on pl.wx). */
  fx: number; fz: number;
  /** The precise proxy once known (the in-place upgrade sets it); null = the pad box below. */
  holder: { proxy: ColliderProxy | null };
  /** Pad-box fallback (pre-scaled half extents + its transform). */
  box: { half: [number, number, number]; t: { x: number; y: number; z: number; yaw: number; scale: number } };
  /** R10 collider ring: the Rapier body exists only while the player is within walking range. */
  active: boolean;
  /** An enterable the player is inside: no collider until restore(). */
  suppressed: boolean;
}

interface SiteBuild {
  instanceIds: number[];    // StaticInstancer handles (buildings + props + landmarks)
  ghostIds: number[];       // pipeline-warm ghost instances — kept until finishAssembly (batch-survival law)
  roadGroup: THREE.Group;   // road + sidewalk meshes (we own + dispose these geometries)
  colliderIds: string[];    // physics collider ids
  enterableIds: string[];   // building-interior registry entries (foundation wave)
  releases: Array<() => void>; // asset release closures matched to the cache key each load acquired
  /** R8: per-building screen-space LOD records (civ buildings + landmarks; props excluded). */
  lodBuildings: BuildingLodRec[];
  /** L4: the site's buildings as occluders (footprint circle + top) for the line-of-sight march. */
  occluders: SpatialHash2D<Occluder>;
  /** R8: masters the refinement loaded on demand, keyed `key#lod`, refcounted by the buildings showing them. */
  lodMasters: Map<string, LodMaster>;
  /** R8/S1/S5: crossfades in flight — the incoming level's instances (born at fade 0) and the outgoing ones; the fade starts once the incoming batches are RESIDENT and removes the outgoing at the end. */
  fades: Array<{ inGids: number[]; outGids: number[]; ready: boolean; started: number; /** L9: the ledger entry to complete when the crossfade starts. */ ledger?: { key: string; site: number; level: number; metrics: LoadMetrics; queueMs: number; loadMs: number; uploadBytes: number; uploadTasks: number; swapAt: number } }>;
  /** Every colliding placement, for the in-place box → hull upgrade inside the detail band. */
  colliders: SiteCollider[];
  labelData: { key: string; x: number; y: number; z: number }[]; // per-building name + label anchor
  labelSprites: THREE.Sprite[]; // active name labels (empty unless the debug toggle is on)
  dressingGroups: THREE.Group[]; // procedural dressing (walls/gates/wells/fields/…) — owned geometry
  seatIds: string[];        // NPC keep-out seat registrations (structure_seat)
  rotors: THREE.Object3D[]; // windmill rotors, spun on the shared clock
}

/** A settlement mid-assembly — placements instanced a few per frame (freeze fix). */
interface AssemblyJob {
  site: SettlementSite;
  seed: string;
  plan: CityPlan;
  /** prefab key → render asset id (R8 loads other levels of a placed building by id) */
  info: Map<string, bigint>;
  masters: Map<string, THREE.Object3D>;
  placements: Array<{ key: string; wx: number; wz: number; yaw: number; collide: boolean; kind: 'building' | 'landmark' | 'prop'; pw?: number; pd?: number; padH?: number; tH?: number; fixedScale?: number; targetH?: number; fn?: string }>;
  build: SiteBuild;
  /** R10: the overlap test's index (was an O(n²) array scan). */
  placed: SpatialHash2D<Placed>;
  /** R10 settle report accumulators (ms). */
  tOverlap: number; tCollider: number; collidersNow: number; collidersDeferred: number;
  dressAnchors: BuildingAnchor[]; // per-building placed transforms (frontage/yard dressing)
  lampAnchors: LampAnchor[];
  ci: number;
  i: number;
  /** Placement outcome tallies (probe/diagnostics — see assemblyStats). */
  placedN: number; ladderN: number; skipN: number; noMasterN: number;
  /** Terrain flattening rects for enterable placements (registered at finish). */
  stamps: BuildingStamp[];
}

export class CommonsSettlementsModule implements IModule {
  readonly name = 'CommonsSettlements';
  private scene!: THREE.Scene;
  private appRef: App | null = null; // renderer access for the compile pre-warm
  /** siteId → current build stage — the __probe diagnostics read this to name
   *  exactly where a stuck settlement build is parked (plan/resolve/load/warm). */
  readonly buildStage = new Map<number, string>();
  /** siteId → last assembly's placement tallies (probe/diagnostics): buildings+
   *  landmarks placed (full-scale or via the shrink ladder), skipped by
   *  overlap, and dropped for a missing master (load timeout). */
  readonly assemblyStats = new Map<number, { placed: number; ladder: number; skipped: number; noMaster: number }>();
  /** siteId → LOD the current build used (drives the boundary re-assembly). */
  private builtLod = new Map<number, 0 | 1>();
  private settleRoot = new THREE.Group();
  private instancer: StaticInstancer | null = null;
  private roadMat: THREE.MeshStandardMaterial | null = null;
  private sidewalkMat: THREE.MeshStandardMaterial | null = null;

  private sites: SettlementSite[] | null = null;
  private seed = '';
  private frame = 0;
  /** prefabKey → cooked collider proxy (null = none/failed → bbox-box fallback).
   *  Session-cached; the JSONs are small (trimeshes are cooked to a tri budget). */
  private readonly proxies = new Map<string, ColliderProxy | null>();
  /** Living spaceport ships: one animated clone per in-range city, cycling
   *  grounded → liftoff → space → landing on the EXACT pad spot. */
  private readonly ships = new Map<number, {
    group: THREE.Object3D; x: number; homeY: number; z: number; yaw: number; scale: number;
    offset: number; colliderOn: boolean; proxy: ColliderProxy | null; half: [number, number, number];
  }>();
  /** prefabKey → sampled convex hull of the proxy (perf sweep 2026-07-24):
   *  the numbered civ TOWERS are convex-ish, and Rapier hulls cost a fraction
   *  of trimeshes (QBVH memory + build — a city carried ~150 × ~10k-tri
   *  trimeshes). Enterable complexes keep the exact trimesh. */
  private readonly hulls = new Map<string, ColliderProxy>();
  private readonly built = new Map<number, SiteBuild>();   // site.id → built geometry
  private readonly assemblyQueue: AssemblyJob[] = [];      // time-sliced placement jobs
  private readonly dressingQueue: Array<() => void> = [];  // deferred dressing builds (one per throttled tick)
  /** Generated hinterland stand-ins instanced so far, per key (diagnostic), and the placements the builders asked for. */
  private readonly hinterTally = new Map<string, number>();
  private readonly hinterRequested = new Map<string, number>();

  /**
   * Instance the generated stand-ins a dressing builder asked for (hinterland prop wave, 2026-09-07):
   * one cached master per key (the tier's prop LOD), one InstancedMesh per mesh part, every instance
   * seated by the master's FULL bbox bottom-centre (the PIVOT law) and scaled to the placement's
   * `targetH` / `len`; `collide` placements get a box collider from the scaled bbox; a `rotor`
   * placement becomes a plain mesh on a centred pivot that the shared clock spins like the sails.
   */
  private async dressPrefabs(build: SiteBuild, site: SettlementSite, group: THREE.Group, placements: PrefabPlacement[], tag: string): Promise<void> {
    const conn = atlas.conn; if (!conn) return;
    const keyToAsset = new Map<string, bigint>();
    for (const pm of conn.db.prefabModel.iter()) if (pm.prefabKey.startsWith('prop:hinter_')) keyToAsset.set(pm.prefabKey, pm.assetId);
    const byKey = new Map<string, PrefabPlacement[]>();
    for (const p of placements) { const a = byKey.get(p.key) ?? []; a.push(p); byKey.set(p.key, a); }
    for (const [k, a] of byKey) this.hinterRequested.set(k, (this.hinterRequested.get(k) ?? 0) + a.length);
    const UP = new THREE.Vector3(0, 1, 0);
    let ci = 0;
    for (const [key, list] of byKey) {
      const assetId = keyToAsset.get(key); if (assetId === undefined) continue;
      let loaded: { object: THREE.Object3D; release: () => void };
      try { loaded = await this.assets.loadRepresentation(assetId, propLod(activePreset())); } catch { continue; }
      if (this.built.get(site.id) !== build) { loaded.release(); return; } // torn down while loading
      build.releases.push(loaded.release);
      const master = loaded.object; master.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(master); const size = new THREE.Vector3(); bb.getSize(size); const ctr = new THREE.Vector3(); bb.getCenter(ctr);
      if (!(size.y > 1e-3) || !(size.x > 1e-3)) continue;
      const parts = collectMeshParts(master);
      const mats: THREE.Matrix4[] = [];
      for (const p of list) {
        const sc = p.len ? p.len / size.x : (p.targetH ?? size.y) / size.y; const sy = sc * (p.hMul ?? 1);
        if (p.rotor) {
          const pivot = new THREE.Group(); pivot.position.set(p.x, p.y, p.z); pivot.rotation.y = p.yaw;
          const local = new THREE.Matrix4().makeScale(sc, sc, sc).multiply(new THREE.Matrix4().makeTranslation(-ctr.x, -ctr.y, -ctr.z));
          for (const part of parts) { const mesh = new THREE.Mesh(part.geometry, part.material); mesh.userData.sharedAsset = true; mesh.applyMatrix4(new THREE.Matrix4().multiplyMatrices(local, part.local)); pivot.add(mesh); }
          group.add(pivot); build.rotors.push(pivot);
          continue;
        }
        const m = new THREE.Matrix4().compose(new THREE.Vector3(p.x, p.y, p.z), new THREE.Quaternion().setFromAxisAngle(UP, p.yaw), new THREE.Vector3(sc, sy, sc));
        m.multiply(new THREE.Matrix4().makeTranslation(-ctr.x, -bb.min.y, -ctr.z)); // seat by the bbox bottom-centre
        mats.push(m);
        if (p.collide) {
          const hx = (size.x * sc) / 2, hy = (size.y * sy) / 2, hz = (size.z * sc) / 2;
          const cid = `dress:${tag}:${site.id}:p${ci++}`;
          this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [Math.max(0.05, hx), Math.max(0.05, hy), Math.max(0.05, hz)] }, { x: p.x, y: p.y + hy, z: p.z, yaw: p.yaw, scale: 1 });
          build.colliderIds.push(cid);
        }
      }
      this.hinterTally.set(key, (this.hinterTally.get(key) ?? 0) + list.length);
      for (const part of parts) {
        if (!mats.length) break;
        pinStaticMotionVectors(part.material as THREE.Material); // static structure parts (three's frozen previous instance matrices)
        const im = new THREE.InstancedMesh(part.geometry, part.material, mats.length);
        for (let i = 0; i < mats.length; i++) im.setMatrixAt(i, new THREE.Matrix4().multiplyMatrices(mats[i], part.local));
        im.instanceMatrix.needsUpdate = true; im.castShadow = false; im.receiveShadow = true;
        im.userData.sharedAsset = true; // geometry + material belong to the asset cache
        group.add(im);
        if (this.appRef) void warmThenShow(this.appRef, im); // R4: hidden until its pipeline is warm
      }
    }
  }
  private readonly building = new Set<number>();           // sites mid-async-build (no double build)

  /** Far tier (R7): the proxy module hides a site's massing once its real set is built. */
  isBuilt(siteId: number): boolean { return this.built.has(siteId); }
  isBuilding(siteId: number): boolean { return this.building.has(siteId); }
  // City plans are deterministic (seeded from the site) but cost real main-thread ms to
  // generate — cache them so walking back into a streamed-out settlement never re-runs
  // the generator (a hitch source the deep-research pass flagged). Keyed by seed+site.
  private readonly planCache = new Map<string, CityPlan>();
  // Perf wave 3: plans generate in the bake WORKER (a city plan is a 5–13 s
  // CityGenerator run — the dominant traversal hitch when it ran here).
  private planWorker: Worker | null = null;
  private planWorkerBroken = false;
  private planJobId = 0;
  /** In-flight plan requests: the seed each was issued for rides along (see planFromWorker). */
  private readonly planWaiters = new Map<number, { seed: string; resolve: (plan: CityPlan | null) => void }>();
  private readonly bboxCache = new Map<string, THREE.Box3>(); // prefabKey → natural bbox (scale 1)
  private labelsOn = false;                                // debug "mesh names" toggle
  private readonly labelMats = new Map<string, THREE.SpriteMaterial>(); // prefabKey → cached text sprite mat

  private roadMats: RoadMaterialSet | null = null;
  private roadMatsPromise: Promise<void> | null = null;

  constructor(private readonly assets: AssetModule, private readonly physics: PhysicsModule) {}

  /** Load the four street-surface materials ONCE (shared for every settlement;
   *  teardown disposes geometries only). Missing textures degrade to flat
   *  colors — the partial-catalog self-heal. */
  private ensureRoadMats(): Promise<void> {
    if (this.roadMatsPromise) return this.roadMatsPromise;
    this.roadMatsPromise = (async () => {
      const mk = (color: number, rough: number): THREE.MeshStandardMaterial => {
        const m = new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.04 });
        m.polygonOffset = true;
        m.polygonOffsetFactor = -1;
        m.polygonOffsetUnits = -1;
        return m;
      };
      const paved = mk(0x8e9096, 0.94);
      const plate = mk(0x8a939c, 0.82);
      const dirt = mk(0x9a866d, 1.0);
      const walk = mk(0xa9a49a, 0.96);
      const tex = async (key: string, mat: THREE.MeshStandardMaterial, nStrength: number): Promise<void> => {
        try {
          const t = await this.assets.loadTexture(key);
          mat.map = t;
          const n = normalFromAlbedo(t, nStrength);
          if (n) { mat.normalMap = n; mat.normalScale = new THREE.Vector2(0.6, 0.6); }
          mat.color = new THREE.Color(0xffffff);
          mat.needsUpdate = true;
        } catch { /* flat color fallback */ }
      };
      await Promise.all([
        tex('textures/crete_panel.png', paved, 2.2),
        tex('textures/hex_floor.png', plate, 2.6),
        tex('textures/packed_dirt.png', dirt, 2.0),
        tex('textures/plaza_paver.png', walk, 2.4),
      ]);
      this.roadMats = { paved, plate, dirt, walk };
    })();
    return this.roadMatsPromise;
  }

  init(app: App): void {
    this.appRef = app;
    this.scene = app.world.scene;
    this.scene.add(this.settleRoot);
    this.instancer = new StaticInstancer(this.scene, 4096, 'settlements');
    this.instancer.setRainLayer(true); // buildings are roofs for the rain-shelter bake (render wave 1)
    // Settlement buildings cast and receive CSM shadows (2026-09-14). Nothing
    // used to set these, so three's defaults (false/false) meant a whole town
    // was lit flat: no shadow under any eave, and no building shadow falling
    // across the street or onto another building. Cast only where the tier
    // actually has CSM — the preset's `shadowsEnabled` is the #2 GPU lever and
    // adding every building to four cascades is exactly what it exists to
    // control. Receiving is on regardless: without it a building stays bright
    // inside the shadow of anything else.
    // Both flags follow the tier's CSM switch. Receiving with nothing casting
    // buys nothing and still compiles shadow sampling into the material — on a
    // tier with no shadow-casting light that is pure cost at best (2026-09-14).
    const csm = activePreset().shadowsEnabled !== false;
    this.instancer.setShadows(csm, csm);
    this.roadMat = new THREE.MeshStandardMaterial({ color: 0x26262b, roughness: 0.92, metalness: 0 });
    this.sidewalkMat = new THREE.MeshStandardMaterial({ color: 0x6a6a70, roughness: 0.9, metalness: 0 });
    // Streams in the RenderSub phase right after the terrain-chunk streamer (@35).
    app.world.addSystem(makeSystem('CommonsSettlements', Phase.RenderSub, 36, () => this.tick()));
    app.world.addSystem(makeSystem('SettlementShips', Phase.RenderSub, 37, () => this.updateShips()));
    // Dressing life: window/lantern glow follows the night, windmill sails
    // turn on the shared wall clock (every client sees the same phase).
    app.world.addSystem(makeSystem('SettlementDressing', Phase.RenderSub, 38, () => {
      const night = uNightFactor.value as number;
      const nm = dressingNightMats();
      (nm.glow as THREE.MeshBasicMaterial).opacity = night * 0.85;
      (nm.lantern as THREE.MeshBasicMaterial).opacity = night * 0.9;
      if (this.frame % 2 === 0) {
        const t = Date.now() / 1000;
        for (const build of this.built.values()) {
          for (const rotor of build.rotors) rotor.rotation.z = t * 0.55;
        }
      }
    }));
    // Diagnostic: settlement build progress (probe/debug — pairs with __intDiag).
    // Probe hook (fast-approach wave): run the streaming decision NOW,
    // ignoring the frame gate — a headless probe's frames are seconds long,
    // so its approach test drives the tick per teleport hop instead.
    (window as unknown as Record<string, unknown>).__settlementsTick = () => this.tick(true);
    // L1/L2 probe lever: promote every resident LOD0 key's skin regardless of
    // the touch band (headless cameras never fill the viewport) — proves the
    // shape → skin mechanics; returns the keys it started.
    (window as unknown as Record<string, unknown>).__settlementsSkin = (cap?: number) => {
      const preset = activePreset();
      const c = cap ?? refineTextureCap(preset.maxTextureRes, preset.settlementRadiusScale);
      const started: string[] = [];
      for (const [siteId, build] of this.built) {
        for (const [mk, lm] of build.lodMasters) {
          if (!mk.endsWith('#0') || lm.loading || !lm.object) continue;
          const key = mk.slice(0, -2);
          if (this.skins.has(key)) continue;
          const rec = build.lodBuildings.find((r) => r.key === key);
          if (!rec) continue;
          const entry: NonNullable<ReturnType<typeof this.skins.get>> = { assetId: rec.assetId, siteId, cap: c, state: 'loading', lastWantedAt: performance.now(), wanted: true, failedAt: 0 };
          this.skins.set(key, entry); this.skinInflight++; this.refineStats.skinLoads++; started.push(key);
          void this.assets.promoteSkins(lm.object, c, () => this.assets.loadDonorScene(rec.assetId, c)).then((r) => {
            if (this.skins.get(key) !== entry) return;
            if (r.promoted > 0 || r.failed === 0) { entry.state = 'resident'; this.refineStats.skinPromoted += r.promoted; this.refineStats.skinBytesMb = +(this.refineStats.skinBytesMb + r.bytes / 1048576).toFixed(1); }
            else { entry.state = 'failed'; entry.failedAt = performance.now(); this.refineStats.skinFailed++; }
          }).catch(() => { if (this.skins.get(key) === entry) { entry.state = 'failed'; entry.failedAt = performance.now(); this.refineStats.skinFailed++; } })
            .finally(() => { this.skinInflight--; });
        }
      }
      return { started, cap: c };
    };
    (window as unknown as Record<string, unknown>).__settleReport = () => ({ last: settleReports.last, sites: [...settleReports.bySite.values()] });
    // Hinterland prop wave diagnostic: generated stand-ins instanced per key across the built sites.
    (window as unknown as Record<string, unknown>).__hinterDiag = () => ({ keys: Object.fromEntries(this.hinterTally), requested: Object.fromEntries(this.hinterRequested), sites: this.built.size });
    (window as unknown as Record<string, unknown>).__settlements = () => {
      const px = atlas.localX, pz = atlas.localZ;
      const near = (this.sites ?? []).map((s) => ({ id: s.id, tier: s.tier, d: Math.round(Math.hypot(s.centerX - px, s.centerZ - pz)) }))
        .sort((a, b) => a.d - b.d).slice(0, 3);
      return {
        sites: this.sites?.length ?? -1, near,
        built: this.built.size, building: [...this.building],
        stage: [...this.buildStage.entries()], queue: this.assemblyQueue.length,
        catalog: atlas.conn ? [...atlas.conn.db.prefabModel.iter()].length : -1,
        stats: [...this.assemblyStats.entries()].map(([id, s]) => `${id}:p${s.placed}/l${s.ladder}/s${s.skipped}/nm${s.noMaster}`),
        bstamps: buildingStampsNear(-1e12, -1e12, 1e12, 1e12).length,
        builtIds: [...this.built.keys()],
        builtLods: [...this.builtLod.entries()],
        ledger: { summary: lod0Ledger.summary(), line: lod0Ledger.line(), last: lod0Ledger.entries.slice(-5) },
        // Unified draw distance + altitude-aware distance (the fly-away fix):
        cityRadius: Math.max(RENDER_RADIUS.city * activePreset().settlementRadiusScale, currentDrawDistance()),
        cityBuildRadius: RENDER_RADIUS.city * activePreset().settlementRadiusScale,
        drawDistance: currentDrawDistance(),
        // Fast-approach wave: the ladder in force and the player's smoothed ground velocity.
        bands: settlementBands('city', currentDrawDistance(), activePreset().settlementRadiusScale, activePreset().settlementForceLowLod, activePreset().distantDetail !== false),
        motion: playerVelocity(),
        // R8/R9: per-building level histogram over built sites + refinement counters.
        lodHist: ((): Record<string, number> => { const h: Record<string, number> = { lod0: 0, lod1: 0, lod2: 0, lod3: 0 }; for (const b of this.built.values()) for (const r of b.lodBuildings) h[`lod${r.lod}`]++; return h; })(),
        refine: { ...this.refineStats },
        colliderRing: { ...this.ringStats },
        settle: settleReports.last,
        // 3-D distance to the nearest site (altitude folded in) vs the old XZ-only:
        near3d: (this.sites ?? []).map((sg) => ({ id: sg.id, d3: Math.round(Math.hypot(sg.centerX - px, Math.max(0, atlas.localY - 50), sg.centerZ - pz)) }))
          .sort((a, b) => a.d3 - b.d3).slice(0, 3),
      };
    };
    // Diagnostic: the Unbuilt Town dressing layer per built site (probe).
    (window as unknown as Record<string, unknown>).__dressing = () => ({
      queue: this.dressingQueue.length,
      sites: [...this.built.entries()].map(([id, b]) => ({
        id,
        parts: b.dressingGroups.reduce((n, g) => n + g.children.length, 0),
        seats: b.seatIds.length,
        rotors: b.rotors.length,
      })),
    });
    // Diagnostic: per-city ship phase (alt above pad, visibility, collider).
    (window as unknown as Record<string, unknown>).__ships = () =>
      [...this.ships.entries()].map(([id, s]) => {
        const bb = this.bboxCache.get(SKY_SHIP_PREFAB);
        return {
          site: id, alt: Math.round(s.group.position.y - s.homeY), visible: s.group.visible, collider: s.colliderOn,
          // The pad is where the hull's BOTTOM sat at home: homeY + min.y·scale
          // (the same term hullBottomY adds — a minus here read 1687 m lifts).
          padY: Math.round(s.homeY + (bb ? bb.min.y * s.scale : 0)),
          hullBottomY: bb ? Math.round(s.group.position.y + bb.min.y * s.scale) : null,
          hullTopY: bb ? Math.round(s.group.position.y + bb.max.y * s.scale) : null,
          hullLenM: bb ? Math.round(Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) * s.scale) : null,
        };
      });
  }

  private commons(conn: DbConnection): { worldId: bigint; seed: string } | null {
    // PK lookup once resolved — tick() asks ~5×/s and the world table is one row per account.
    const known = atlas.commonsWorldId !== null ? conn.db.world.worldId.find(atlas.commonsWorldId) : undefined;
    if (known) return { worldId: known.worldId, seed: known.terrainSeed };
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return { worldId: w.worldId, seed: w.terrainSeed };
    return null;
  }

  /** Fetch a cook-time-baked plan (vite public/plans). Null → not baked. */
  private async planFromStatic(site: SettlementSite, seed: string): Promise<CityPlan | null> {
    try {
      const res = await fetch(`/plans/${planFileFor(seed, site.id)}`);
      if (!res.ok) return null;
      const { siteSeed, plan } = JSON.parse(await res.text(), (_k, v) =>
        v && typeof v === 'object' && v.__ta === 'Float64Array' ? new Float64Array(v.d)
        : v && typeof v === 'object' && v.__ta === 'Float32Array' ? new Float32Array(v.d) : v,
      ) as { siteSeed: number; plan: CityPlan };
      primeSettlementPlan(seed, siteSeed, plan); // stampedHeight shares the memo
      return plan;
    } catch { return null; }
  }

  /** Declare the plan worker dead and flush every waiter to the sync fallback. */
  private failPlanWorker(): void {
    this.planWorkerBroken = true;
    for (const w of this.planWaiters.values()) w.resolve(null);
    this.planWaiters.clear();
    this.planWorker?.terminate();
    this.planWorker = null;
  }

  /** Request a settlement plan from the bake worker (null → caller falls back sync). */
  private planFromWorker(site: SettlementSite, seed: string): Promise<CityPlan | null> {
    if (this.planWorkerBroken) return Promise.resolve(null);
    if (!this.planWorker) {
      try {
        this.planWorker = new Worker(new URL('../workers/water_bake_worker.ts', import.meta.url), { type: 'module' });
        this.planWorker.onerror = () => this.failPlanWorker();
        this.planWorker.onmessage = (e: MessageEvent<{ kind: string; id: number; siteSeed: number; plan: unknown | null }>) => {
          if (e.data.kind !== 'plan') return;
          const waiter = this.planWaiters.get(e.data.id);
          if (!waiter) return;
          this.planWaiters.delete(e.data.id);
          const plan = e.data.plan as CityPlan | null;
          try {
            // Prime the main-thread memo too — the stampedHeight sync
            // fallback shares plans through settlement_plan's cache. Keyed by
            // the seed THIS request was issued for: the handler is installed
            // once, so the enclosing call's `seed` went stale after a reseed.
            if (plan) primeSettlementPlan(waiter.seed, e.data.siteSeed, plan);
          } finally {
            waiter.resolve(plan); // never leave buildSettlement's await hanging (the 60 s deadline can't see a deleted waiter)
          }
        };
      } catch {
        this.planWorkerBroken = true;
        this.planWorker = null;
        return Promise.resolve(null);
      }
    }
    const worker = this.planWorker;
    if (!worker) return Promise.resolve(null);
    return new Promise((resolve) => {
      const id = ++this.planJobId;
      // Reply deadline: a worker wedged mid-import (cold dev-server dep-optimize)
      // never fires 'error', and this await would hang the site's build forever.
      // 60 s clears even several queued 5–13 s city plans; a trip costs only the
      // sync-generation fallback, never a stuck settlement.
      const deadline = setTimeout(() => { if (this.planWaiters.has(id)) this.failPlanWorker(); }, 60_000);
      this.planWaiters.set(id, { seed, resolve: (plan) => { clearTimeout(deadline); resolve(plan); } });
      worker.postMessage({ kind: 'plan', id, seed, siteId: site.id });
    });
  }

  /** Boot loading screen: settlements within render range of the player vs
   *  those fully built (assembling counts sites mid-build + queued placement jobs). */
  loadReadiness(): { resolved: boolean; inRange: number; built: number; assembling: number; frac: number } {
    // resolved:false until the Commons seed lands and the site list derives —
    // the loading gate once read the pre-resolution {inRange:0} as "no
    // structures needed" and cleared while the spawn city was still assembling
    // ("the buildings do not load in", desktop + Quest field reports).
    if (!this.sites || atlas.currentWorldId !== atlas.commonsWorldId) {
      return { resolved: false, inRange: 0, built: 0, assembling: 0, frac: 0 };
    }
    let inRange = 0;
    let builtN = 0;
    let fracSum = 0;
    // Count by the BUILD band the tick actually builds inside (scaled by the
    // preset and the draw distance), not the unscaled RENDER_RADIUS: on
    // potato/low a second site 500–1000 m from spawn could never build, the
    // loader showed 1/2 until the 75 s stall timeout (2026-09-12).
    const rPreset = activePreset();
    const rDraw = currentDrawDistance();
    const rBands: Partial<Record<string, ReturnType<typeof settlementBands>>> = {};
    for (const site of this.sites) {
      const dist = Math.hypot(site.centerX - atlas.localX, site.centerZ - atlas.localZ);
      const bands = rBands[site.tier] ?? (rBands[site.tier] = settlementBands(site.tier, rDraw, rPreset.settlementRadiusScale, rPreset.settlementForceLowLod, rPreset.distantDetail !== false));
      if (dist >= bands.build) continue;
      inRange++;
      if (this.built.has(site.id)) { builtN++; fracSum += 1; continue; }
      // Mid-assembly: read the stage string ("load:N/M" → first 80%,
      // "warm:N/M" → last 20%) so the loading bar reflects real progress.
      const st = this.buildStage.get(site.id);
      const m = st ? /:(\d+)\/(\d+)/.exec(st) : null;
      if (m && Number(m[2]) > 0) {
        const p = Number(m[1]) / Number(m[2]);
        fracSum += st!.startsWith('warm') ? 0.8 + 0.2 * p : 0.8 * p;
      }
    }
    return {
      resolved: true,
      inRange,
      built: builtN,
      assembling: this.building.size + this.assemblyQueue.length,
      frac: inRange > 0 ? fracSum / inRange : 1,
    };
  }

  /** S7: the previous frame's duration (ms) and the device's baseline (EMA), measured between ticks (which run every frame). */
  private lastFrameMs = 0;
  private frameEmaMs = 0;
  private lastTickAt = 0;
  private lastSwapTickAt = 0;

  private tick(force = false): void {
    {
      const now = performance.now();
      if (this.lastTickAt > 0) {
        this.lastFrameMs = now - this.lastTickAt;
        // Slow EMA (~2 s at 60 fps): the machine's normal frame, so pacing reacts to SPIKES, not to a slow device.
        this.frameEmaMs = this.frameEmaMs === 0 ? this.lastFrameMs : this.frameEmaMs + (this.lastFrameMs - this.frameEmaMs) * 0.01;
      }
      this.lastTickAt = now;
    }
    this.stepFades();
    // Left the Commons → tear everything down on the next frame (before the throttle).
    if (atlas.currentWorldId !== atlas.commonsWorldId) {
      if (this.assemblyQueue.length > 0) this.abortAssemblies();
      if (this.built.size > 0) this.resetBuilds();
      return;
    }
    this.assembleTick(); // time-sliced placement work runs EVERY frame (bounded)
    if (++this.frame % TICK_EVERY !== 0 && !force) return;
    // One deferred dressing build per throttled tick (~5 Hz): spreads the
    // meshHeight-heavy bursts across frames instead of the assembly frame.
    const dress = this.dressingQueue.shift();
    if (dress) dress();
    const conn = atlas.conn;
    if (!conn || !this.instancer) return;
    const commons = this.commons(conn);
    if (!commons) return;
    if (this.seed !== commons.seed) { this.seed = commons.seed; this.sites = settlementSites(this.seed); }
    if (!this.sites) return;

    const px = atlas.localX;
    const pz = atlas.localZ;
    // ALTITUDE-AWARE distance (the flying pop-out fix): the old XZ-only distance
    // crossed the 380/460 LOD band the instant you flew away horizontally, so a
    // city churned LOD0↔LOD1 (dispose→rebuild = a visible vanish) while cruising.
    // Folding altitude in makes a city you fly UP from read as genuinely FAR, so
    // it settles at LOD1 and STAYS there — no churn. The −50 m ignores on-ground
    // terrain-height so foot distances are unchanged. Cities render as far as the
    // terrain now: stream radius = max(tier radius, the draw-distance setting).
    // Tier scaling (potato 0.6, others 1): settlement content is instanced and
    // outside farCull's reach, so the streaming radius is the only lever on how
    // much draw-set is resident. Scale must keep city×scale above the ~420 m
    // plan footprint or you stand inside an unbuilt city.
    const preset = activePreset();
    const radiusScale = preset.settlementRadiusScale;
    const forceLowLod = preset.settlementForceLowLod;
    const alt = Math.max(0, atlas.localY - 50);
    const drawDist = currentDrawDistance();
    // Closing-speed lookahead (fast-approach wave): sample the player's ground
    // velocity once per tick; every threshold below compares the EFFECTIVE
    // distance (raw − closing × 15 s) except stream-out, which stays raw.
    samplePlayerMotion(performance.now());
    const vel = playerVelocity();
    const bandsOf: Partial<Record<SettlementTier, SettlementBands>> = {};
    for (const site of this.sites) {
      const dist = Math.hypot(site.centerX - px, alt, site.centerZ - pz);
      const bands = bandsOf[site.tier] ?? (bandsOf[site.tier] = settlementBands(site.tier, drawDist, radiusScale, forceLowLod, preset.distantDetail !== false));
      const closing = closingSpeed(vel.vx, vel.vz, site.centerX - px, site.centerZ - pz);
      const eff = effectiveDistance(dist, closing);
      // Build IN inside the build band (¾ of the draw distance or the tier
      // radius, whichever is larger — the far ring already draws the same LOD1
      // silhouettes there), keep a settlement RESIDENT out to the draw distance
      // (cities render as far as terrain and never vanish while you fly away).
      if (eff < bands.build && !this.built.has(site.id) && !this.building.has(site.id)) {
        void this.buildSettlement(site);
      } else if (dist > bands.dispose && this.built.has(site.id)) {
        this.disposeSettlement(site.id);
      } else if (this.built.has(site.id)) {
        // LOD is STICKY + UPGRADE-ONLY (the fly-away pop-out fix). A LOD swap is a
        // full disposeSettlement→async-rebuild, so it is a visible VANISH for the
        // rebuild's duration. We therefore only ever UPGRADE (LOD1→LOD0) once a
        // city comes inside the detail radius, and NEVER downgrade mid-flight —
        // flying away from a city keeps it exactly as built until it leaves the
        // draw distance and disposes cleanly (behind you). No downgrade ⇒ one-way
        // ⇒ no thrash, so no hysteresis band is needed.
        const bl = this.builtLod.get(site.id);
        // While the loading gate is up, boot-first-builds are LOD1 by law —
        // upgrading them would dispose→rebuild in a loop under the gate
        // (rebuilds re-pick LOD1) and push the gate out indefinitely.
        // forceLowLod (potato): the one place a downgrade is still wanted — LOD0
        // triangles are the exact cost the tier exists to avoid, so a city built
        // at LOD0 (tier switched mid-session) downgrades once to LOD1 and stays.
        // In-place COLLIDER upgrade (boxes → cooked hulls) inside the detail
        // band — the old dispose→rebuild at LOD0 (a visible vanish plus the
        // whole city's LOD0 files) is gone; visuals refine per building (R8).
        if (!forceLowLod && !atlas.loadingGate && bl === 1 && eff < bands.near) this.upgradeColliders(site.id);
      }
      // Warm ring: prefetch the plan + this site's civ-pack GLBs one band OUT
      // from the build radius, so crossing it assembles from cache instead of
      // cold-loading 30-50 GLBs on the approach hot path.
      if (eff >= bands.build && eff < bands.warm
          && !this.built.has(site.id) && !this.building.has(site.id)
          && !this.warmingSites.has(site.id) && !this.warmHeld.has(site.id)) {
        void this.warmSettlement(site);
      } else if (this.warmHeld.has(site.id)
                 && (this.built.has(site.id) || dist > bands.warm + HYSTERESIS)) {
        // Built sites hold their own refs (the build's loads hit our warm
        // cache, then take independent refcounts) — the warm refs can go.
        this.releaseWarm(site.id);
      }
    }
    this.refineLods(vel);
    this.syncColliderRing();
  }

  // ── R10: the collider ring (Rapier bodies only within walking range) ──
  private ringAdd(c: SiteCollider): void {
    if (c.active || c.suppressed) return;
    if (c.holder.proxy) this.physics.addStaticCollider(c.cid, c.holder.proxy, { x: c.px, y: c.py, z: c.pz, yaw: c.yaw, scale: c.scale });
    else this.physics.addStaticCollider(c.cid, { shape: 'box', halfExtents: c.box.half }, c.box.t);
    c.active = true;
  }
  private ringRemove(c: SiteCollider): void {
    if (!c.active) return;
    this.physics.removeStaticCollider(c.cid);
    c.active = false;
  }
  /** Ring counters (probe: `__settlements().colliderRing`). */
  readonly ringStats = { active: 0, total: 0, adds: 0, drops: 0 };
  private syncColliderRing(): void {
    const px = atlas.localX, pz = atlas.localZ;
    let active = 0, total = 0;
    const builds: SiteBuild[] = [...this.built.values(), ...this.assemblyQueue.map((j) => j.build)];
    for (const build of builds) {
      for (const c of build.colliders) {
        total++;
        // FOOTPRINT distance (fx/fz), never the body origin — see SiteCollider.fx.
        const want = colliderRingWantFor(c, px, pz);
        if (want && !c.active && !c.suppressed) { this.ringAdd(c); this.ringStats.adds++; }
        else if (!want && c.active) { this.ringRemove(c); this.ringStats.drops++; }
        if (c.active) active++;
      }
    }
    this.ringStats.active = active; this.ringStats.total = total;
  }

  // ── R8: screen-space LOD selection per building, with a downgrade path ──
  /** Refinement counters (probe: `__settlements().refine`). */
  readonly refineStats = { swaps: 0, loads: 0, pending: 0, inflight: 0, lod0Swaps: 0, colliderUpgrades: 0, prefetched: 0, prefetchInflight: 0, fadesStarted: 0, fadesDone: 0, pacedTicks: 0, dwellSkips: 0, lastFrameMs: 0, frameEmaMs: 0,
    /** L1/L2 skins: keys whose 1024/2048 levels are wanted (touch band), loads started, textures promoted/failed/demoted, resident keys, loads in flight, skin bytes fetched. */
    skinWanted: 0, skinLoads: 0, skinPromoted: 0, skinFailed: 0, skinDemoted: 0, skinResident: 0, skinInflight: 0, skinBytesMb: 0, shapeCap: 0, skinCap: 0, skinBandOn: 0, skinPrefetched: 0,
    /** L4: upgrades skipped because the building is occluded / outside the view cone this tick, LOD0 buildings demoted after OCCLUDED_DEMOTE_MS hidden. */
    occludedSkips: 0, occludedDemotes: 0, coneDeferred: 0,
    /** Occlusion draw culling: buildings faded out / restored. */
    occlusionHidden: 0, occlusionShown: 0 };
  /** L1: per-key skin state (a key's textures are shared by every site showing it, so the skin is global). */
  private readonly skins = new Map<string, { assetId: bigint; siteId: number; cap: number; state: 'loading' | 'resident' | 'failed'; lastWantedAt: number; wanted: boolean; failedAt: number; donorRelease?: () => void }>();
  private skinInflight = 0;
  private readonly tmpFwd = new THREE.Vector3();
  /** L8: keys whose skins were prefetched (once per key while the key stays unskinned). */
  private readonly skinPrefetched = new Set<string>();
  /** A level no building shows is released this long after its last user left. */
  private static readonly MASTER_IDLE_MS = 60_000;
  private static readonly REFINE_SWAPS_PER_TICK = 6;
  /** LOD0 swaps per tick: each births several new (geometry, material) batches whose uploads ride the warm queue. */
  private static readonly REFINE_LOD0_SWAPS_PER_TICK = 2;
  private static readonly REFINE_LOADS_INFLIGHT = 3;

  /**
   * Upgrade a built settlement's colliders in place: EVERY colliding placement
   * — civ buildings, the spaceport hall, landing pads, solid furniture —
   * swaps its pad box for the cooked proxy (hull for the civ towers, the
   * trimesh for the rest) at the SAME compensated transform. Once per site.
   * (The first cut covered civ buildings only: the boarding hall kept a box
   * that swallowed the spaceport spot — "can't get near it", 2026-09-02.)
   */
  private upgradeColliders(siteId: number): void {
    const build = this.built.get(siteId);
    if (!build || this.builtLod.get(siteId) === 0) return;
    this.builtLod.set(siteId, 0);
    let upgraded = 0;
    // A hidden enterable (the player is inside) has no collider right now —
    // only update its holder so restore() re-adds the hull.
    for (const c of build.colliders) {
      if (c.holder.proxy) continue; // already precise (a build with proxies)
      let proxy = this.proxies.get(c.key) ?? null;
      if (proxy && proxy.shape === 'trimesh_static' && proxy.positions && /_(scifi|city)\d/.test(c.key)) {
        let hull = this.hulls.get(c.key);
        if (!hull) {
          const src = proxy.positions;
          const n = (src.length / 3) | 0;
          const step = Math.max(1, Math.ceil(n / 96));
          const pts: number[] = [];
          for (let i = 0; i < n; i += step) pts.push(src[i * 3], src[i * 3 + 1], src[i * 3 + 2]);
          hull = { shape: 'convex_hull', points: pts };
          this.hulls.set(c.key, hull);
        }
        proxy = hull;
      }
      if (!proxy) continue;
      c.holder.proxy = proxy;
      if (c.suppressed) continue; // inside it: restore() re-adds the hull
      if (c.active) { this.physics.removeStaticCollider(c.cid); c.active = false; this.ringAdd(c); }
      upgraded++;
    }
    this.refineStats.colliderUpgrades += upgraded;
  }

  /**
   * Every tick: measure each built building's screen size at its current AND
   * lookahead-predicted distance, pick its level with hysteresis, and swap the
   * instances of the largest-on-screen candidates first (R9's priority). A
   * level whose master is not resident is LOADED (at most two in flight,
   * biggest first) and swapped on a later tick; a level nobody shows any more
   * is released (the VRAM reclaim the sticky LOD0 city never had).
   */
  private refineLods(vel: { vx: number; vz: number }): void {
    const inst = this.instancer;
    if (!inst) return;
    const preset = activePreset();
    const bias = screenLodBias(preset.settlementRadiusScale);
    const forceLowLod = preset.settlementForceLowLod;
    const cam = (this.appRef?.renderer as unknown as { cameras?: { main?: { fov?: number } } } | undefined)?.cameras?.main;
    const fov = ((cam?.fov ?? 60) * Math.PI) / 180;
    const px = atlas.localX, py = atlas.localY, pz = atlas.localZ;
    // L4: the view direction (the main camera's world forward, XZ) and the eye.
    const camObj = cam as unknown as { getWorldDirection?: (v: THREE.Vector3) => THREE.Vector3 } | undefined;
    const fwd = camObj?.getWorldDirection ? camObj.getWorldDirection(this.tmpFwd) : this.tmpFwd.set(-Math.sin(atlas.localYaw), 0, -Math.cos(atlas.localYaw));
    const fx = fwd.x, fz = fwd.z;
    const eye = { x: px, y: py + 1.6, z: pz };
    const cands: Array<{ build: SiteBuild; rec: BuildingLodRec; target: 0 | 1 | 2 | 3; pri: number }> = [];
    const prefetch: Array<{ build: SiteBuild; rec: BuildingLodRec; target: 0 | 1 | 2 | 3; pri: number }> = [];
    const nowMs = performance.now();
    // L1: a LOD0 swap loads the SHAPE — LOD0 geometry with the 512 px textures
    // LOD1 already shares (geometry only crosses the bus); the 1024/2048
    // levels arrive as skins inside the touch band (refineSkins).
    const skinCap = refineTextureCap(preset.maxTextureRes, preset.settlementRadiusScale);
    const texCap = shapeTextureCap(preset.maxTextureRes);
    this.refineStats.shapeCap = texCap; this.refineStats.skinCap = skinCap;
    for (const [siteId, build] of this.built) {
      if (this.building.has(siteId)) continue;
      // Prefetched / retired levels nobody shows: release after a grace (memory).
      for (const [mk, lm] of build.lodMasters) {
        if (lm.users <= 0 && !lm.loading && lm.idleSince !== undefined && performance.now() - lm.idleSince > CommonsSettlementsModule.MASTER_IDLE_MS) { lm.release(); build.lodMasters.delete(mk); }
      }
      for (const rec of build.lodBuildings) {
        if (rec.hidden || rec.swapping) continue;
        const dx = rec.cx - px, dz = rec.cz - pz;
        const dist = Math.hypot(dx, py - rec.py, dz);
        const closing = closingSpeed(vel.vx, vel.vz, dx, dz);
        const eff = effectiveDistance(dist, closing);
        const sNow = screenSize(rec.h, dist, fov), sPred = screenSize(rec.h, eff, fov);
        let target = screenLodFor(Math.max(sNow, sPred), rec.lod, bias, forceLowLod);
        if ((preset.settlementBuildLod ?? 1) === 2 && target < 2) target = 2; // minimal: the ladder tops out at LOD2
        // L4: occlusion (inside the LOD0 band only) — an occluded building is
        // never upgraded, and one that stays hidden is demoted from LOD0.
        let occluded = false;
        if (dist < OCCLUSION_MAX_DIST_M && (target < rec.lod || rec.lod === 0)) {
          const idx = rec.idx; // stored at build (an indexOf per building per tick was O(B²))
          occluded = occludedByPlan(eye, { id: idx, x: rec.cx, z: rec.cz, groundY: rec.py, h: rec.h }, build.occluders);
          if (occluded) {
            if (rec.occludedSince === 0) rec.occludedSince = nowMs;
            if (target < rec.lod) { this.refineStats.occludedSkips++; target = rec.lod; }
            if (rec.lod === 0 && nowMs - rec.occludedSince >= OCCLUDED_DEMOTE_MS && dwellOk(nowMs, rec.lastSwapAt)) { target = 1; this.refineStats.occludedDemotes++; }
          } else rec.occludedSince = 0;
        } else rec.occludedSince = 0;
        // OCCLUSION DRAW CULLING (perf frontier 2026-09-08, item 8): a building
        // the plan occluders have hidden for OCCLUSION_HIDE_MS stops drawing
        // (its instances fade to 0 — the dither discards every fragment, so the
        // scene and shadow passes pay no fill), and draws again the tick it is
        // seen. The same occluder set L4 uses to gate LOD upgrades.
        const wantHidden = occluded && rec.occludedSince > 0 && nowMs - rec.occludedSince >= OCCLUSION_HIDE_MS;
        if (wantHidden !== (rec.drawHidden ?? false)) {
          rec.drawHidden = wantHidden;
          for (const gid of rec.gids) this.instancer?.setFade(gid, wantHidden ? 0 : 1);
          if (wantHidden) this.refineStats.occlusionHidden++; else this.refineStats.occlusionShown++;
        }
        if (target !== rec.lod) {
          // S7 dwell: a building keeps its level for SWAP_DWELL_MS after a swap.
          if (!dwellOk(nowMs, rec.lastSwapAt)) { this.refineStats.dwellSkips++; continue; }
          if (rec.candSince === 0) rec.candSince = nowMs; // L9: queue time starts here
          // L4: the foveated cone — what the player looks at loads first.
          const w = viewConeWeight(dx, dz, fx, fz, fov);
          if (w < 1) this.refineStats.coneDeferred++;
          cands.push({ build, rec, target, pri: loadPriority(sNow, sPred) * w }); continue;
        }
        rec.candSince = 0;
        // S2: the level the approach will want TWO lookaheads out — load and
        // make it resident now, swap later at the normal threshold.
        if (closing > 0.5) {
          const sPre = screenSize(rec.h, effectiveDistance(dist, closing, 2 * LOOKAHEAD_S), fov);
          let pre = screenLodFor(sPre, rec.lod, bias, forceLowLod);
          if ((preset.settlementBuildLod ?? 1) === 2 && pre < 2) pre = 2;
          if (pre !== rec.lod && !build.lodMasters.has(`${rec.key}#${pre}`)) prefetch.push({ build, rec, target: pre, pri: sPre });
        }
      }
    }
    this.refineSkins(fov, px, py, pz, skinCap, nowMs, vel);
    this.refineStats.pending = cands.length;
    // S2 prefetch: one load in flight, biggest predicted first, pre-uploaded on arrival.
    if (prefetch.length && this.refineStats.prefetchInflight < 1) {
      prefetch.sort((a, b) => b.pri - a.pri);
      const c = prefetch[0];
      const mk = `${c.rec.key}#${c.target}`;
      const entry: LodMaster = { object: null as unknown as THREE.Object3D, release: () => { /* set on load */ }, users: 0 };
      this.refineStats.prefetchInflight++; this.refineStats.prefetched++;
      const loadP = c.target === 0 ? this.assets.loadTiered(c.rec.assetId, texCap) : this.assets.loadRepresentation(c.rec.assetId, c.target);
      const prefetchSite = [...this.built].find(([, b]) => b === c.build)?.[0];
      entry.loading = loadP.then((r) => {
        // Site gone meanwhile (a fast approach that turned away): release, or the level is pinned for the session (2026-09-12).
        if (prefetchSite === undefined || this.built.get(prefetchSite) !== c.build) { r.release(); c.build.lodMasters.delete(mk); return null; }
        entry.object = r.object; entry.release = r.release; entry.loading = undefined; entry.idleSince = performance.now();
        if (this.appRef) void warmResources(this.appRef, r.object); // resident before it is ever needed
        return entry;
      }).catch(() => { c.build.lodMasters.delete(mk); return null; }).finally(() => { this.refineStats.prefetchInflight--; });
      c.build.lodMasters.set(mk, entry);
    }
    if (cands.length === 0) return;
    cands.sort((a, b) => b.pri - a.pri);
    // S7 pacing: the swap budget follows the previous frame — full when the
    // frame was quick, half when it was busy, none when it was already late
    // (loads keep going; only the main-thread swap work waits a tick).
    let budget = swapBudget(this.lastFrameMs, CommonsSettlementsModule.REFINE_SWAPS_PER_TICK, this.frameEmaMs);
    // Starvation guard: pacing delays swaps, it never denies them.
    if (budget === 0 && nowMs - this.lastSwapTickAt > SWAP_STARVE_MS) budget = 1;
    if (budget < CommonsSettlementsModule.REFINE_SWAPS_PER_TICK) this.refineStats.pacedTicks++;
    this.refineStats.lastFrameMs = +this.lastFrameMs.toFixed(1);
    this.refineStats.frameEmaMs = +this.frameEmaMs.toFixed(1);
    let swaps = 0, lod0Swaps = 0;
    for (const c of cands) {
      if (swaps >= budget) break;
      if (c.target === 0 && lod0Swaps >= CommonsSettlementsModule.REFINE_LOD0_SWAPS_PER_TICK) continue;
      const mk = `${c.rec.key}#${c.target}`;
      const lm = c.build.lodMasters.get(mk);
      if (lm && !lm.loading) { this.swapBuildingLod(c.build, c.rec, c.target, lm, inst); swaps++; this.lastSwapTickAt = nowMs; if (c.target === 0) { lod0Swaps++; this.refineStats.lod0Swaps++; } continue; }
      if (lm?.loading) continue; // on its way
      if (this.refineStats.inflight >= CommonsSettlementsModule.REFINE_LOADS_INFLIGHT) continue;
      // Load the level (pack-served for 1–3; LOD0 per file), biggest on screen first.
      const entry: LodMaster = { object: null as unknown as THREE.Object3D, release: () => { /* set on load */ }, users: 0, loadStartedAt: performance.now() };
      this.refineStats.inflight++; this.refineStats.loads++;
      const siteId = [...this.built].find(([, b]) => b === c.build)?.[0];
      // LOD0 as the SHAPE (512 px, pack-served); LOD1–3 pack-served.
      const loadP = c.target === 0 ? this.assets.loadTiered(c.rec.assetId, texCap) : this.assets.loadRepresentation(c.rec.assetId, c.target);
      entry.loading = loadP.then((r) => {
        entry.object = r.object; entry.release = r.release; entry.loading = undefined; entry.loadedAt = performance.now(); entry.metrics = r.metrics;
        if (siteId === undefined || this.built.get(siteId) !== c.build) { r.release(); c.build.lodMasters.delete(mk); return null; } // site gone meanwhile
        return entry;
      }).catch(() => { c.build.lodMasters.delete(mk); return null; }).finally(() => { this.refineStats.inflight--; });
      c.build.lodMasters.set(mk, entry);
    }
  }

  /**
   * L1/L2 skin stage: a key whose largest LOD0 building fills the viewport
   * (skinWanted's band) gets its textures promoted to the preset's cap IN
   * PLACE — the shape's textures are the registry's shared objects, so every
   * site showing that key is skinned at once; idle keys and the least
   * recently wanted beyond SKIN_MAX_RESIDENT are demoted back to 512.
   */
  private refineSkins(fov: number, px: number, py: number, pz: number, cap: number, now: number, vel: { vx: number; vz: number } = { vx: 0, vz: 0 }): void {
    // L6: the band comes from the viewport — one texel per pixel over the
    // facade tiling, so a 4K screen asks for the skin at half the fraction.
    const vh = (this.appRef?.renderer as unknown as { native?: { domElement?: { clientHeight?: number } } } | undefined)?.native?.domElement?.clientHeight
      ?? (typeof window !== 'undefined' ? window.innerHeight : 1080);
    const band = skinBandFor(vh);
    this.refineStats.skinBandOn = +band.on.toFixed(3);
    const best = new Map<string, { size: number; pred: number; build: SiteBuild; siteId: number; assetId: bigint }>();
    for (const [siteId, build] of this.built) {
      if (this.building.has(siteId)) continue;
      for (const rec of build.lodBuildings) {
        if (rec.lod !== 0 || rec.hidden) continue;
        const dx = rec.cx - px, dz = rec.cz - pz;
        const dist = Math.hypot(dx, py - rec.py, dz);
        const s = screenSize(rec.h, dist, fov);
        // L8: the size this building will have SKIN_PREFETCH_LOOKAHEADS lookaheads out on a closing approach.
        const closing = closingSpeed(vel.vx, vel.vz, dx, dz);
        const pred = closing > 0.5 ? screenSize(rec.h, effectiveDistance(dist, closing, SKIN_PREFETCH_LOOKAHEADS * LOOKAHEAD_S), fov) : s;
        const b = best.get(rec.key);
        if (!b || s > b.size) best.set(rec.key, { size: s, pred: Math.max(pred, b?.pred ?? 0), build, siteId, assetId: rec.assetId });
        else if (pred > b.pred) b.pred = pred;
      }
    }
    let wantedCount = 0;
    for (const [key, b] of best) {
      const st = this.skins.get(key);
      const wanted = skinWantedAt(b.size, st?.state === 'resident', band);
      if (!wanted) {
        if (st) st.wanted = false;
        // L8: decode the skin ahead of need when the approach will want it.
        else if (b.pred >= band.on) {
          const lm = b.build.lodMasters.get(`${key}#0`);
          if (lm && !lm.loading && lm.object && !this.skinPrefetched.has(key)) { this.skinPrefetched.add(key); this.refineStats.skinPrefetched += this.assets.prefetchSkins(lm.object, cap); }
        }
        continue;
      }
      wantedCount++;
      if (st) {
        st.wanted = true; st.lastWantedAt = now;
        if (st.state === 'failed' && now - st.failedAt > 60_000) this.skins.delete(key); // retry after a minute
        else continue;
      }
      if (this.skinInflight >= SKIN_LOADS_INFLIGHT) continue;
      const lm = b.build.lodMasters.get(`${key}#0`);
      if (!lm || lm.loading || !lm.object) continue;
      const entry: NonNullable<ReturnType<typeof this.skins.get>> = { assetId: b.assetId, siteId: b.siteId, cap, state: 'loading', lastWantedAt: now, wanted: true, failedAt: 0 };
      this.skins.set(key, entry); this.skinInflight++; this.refineStats.skinLoads++;
      const fallback = (): Promise<THREE.Object3D | null> => this.assets.loadDonorScene(b.assetId, cap);
      void this.assets.promoteSkins(lm.object, cap, fallback).then((r) => {
        if (this.skins.get(key) !== entry) return;
        if (r.promoted > 0 || r.failed === 0) { entry.state = 'resident'; this.refineStats.skinPromoted += r.promoted; this.refineStats.skinBytesMb = +(this.refineStats.skinBytesMb + r.bytes / 1048576).toFixed(1); }
        else { entry.state = 'failed'; entry.failedAt = performance.now(); this.refineStats.skinFailed++; }
      }).catch(() => { if (this.skins.get(key) === entry) { entry.state = 'failed'; entry.failedAt = performance.now(); this.refineStats.skinFailed++; } })
        .finally(() => { this.skinInflight--; });
    }
    const resident = [...this.skins].filter(([, s]) => s.state === 'resident').map(([key, s]) => ({ key, lastWantedAt: s.lastWantedAt, wanted: s.wanted }));
    for (const key of skinsToEvict(resident, SKIN_MAX_RESIDENT, now)) {
      const st = this.skins.get(key);
      if (!st) continue;
      const master = this.shapeMasterFor(key, st.siteId);
      if (master) this.refineStats.skinDemoted += this.assets.demoteSkins(master);
      this.skins.delete(key); this.skinPrefetched.delete(key);
    }
    this.refineStats.skinWanted = wantedCount; this.refineStats.skinResident = resident.length; this.refineStats.skinInflight = this.skinInflight;
  }

  /** The resident LOD0 shape master of a key: the site it was skinned from, else any built site showing it. */
  private shapeMasterFor(key: string, siteId: number): THREE.Object3D | null {
    const lm = this.built.get(siteId)?.lodMasters.get(`${key}#0`);
    if (lm?.object && !lm.loading) return lm.object;
    for (const build of this.built.values()) { const m = build.lodMasters.get(`${key}#0`); if (m?.object && !m.loading) return m.object; }
    return null;
  }

  /** Every frame: advance crossfades — complementary dither (lib/lod_fade.ts) over FADE_MS, then retire the outgoing instances. */
  private stepFades(): void {
    const inst = this.instancer;
    if (!inst) return;
    const now = performance.now();
    for (const build of this.built.values()) {
      if (build.fades.length === 0) continue;
      for (let i = build.fades.length - 1; i >= 0; i--) {
        const f = build.fades[i];
        if (!f.ready) continue;
        if (f.started === 0) {
          f.started = now; this.refineStats.fadesStarted++;
          const L = f.ledger;
          if (L) { lod0Ledger.add({ key: L.key, site: L.site, level: L.level, source: L.metrics.source, wireBytes: L.metrics.wireBytes, fetchMs: L.metrics.fetchMs, parseMs: L.metrics.parseMs, queueMs: +L.queueMs.toFixed(0), loadMs: +L.loadMs.toFixed(0), uploadBytes: L.uploadBytes, uploadTasks: L.uploadTasks, swapToFadeMs: +(now - L.swapAt).toFixed(0), at: now }); f.ledger = undefined; }
        }
        const t = Math.min(1, (now - f.started) / FADE_MS);
        const { incoming, outgoing } = fadePair(t);
        for (const g of f.inGids) inst.setFade(g, t >= 1 ? 1 : incoming);
        if (t >= 1) {
          for (const g of f.outGids) inst.remove(g);
          build.fades.splice(i, 1);
          this.refineStats.fadesDone++;
        } else {
          for (const g of f.outGids) inst.setFade(g, outgoing);
        }
      }
    }
  }

  /** Swap one building's instances to `target` from a resident master; the crossfade retires the old ones. */
  private swapBuildingLod(build: SiteBuild, rec: BuildingLodRec, target: 0 | 1 | 2 | 3, lm: LodMaster, inst: StaticInstancer): void {
    const place = new THREE.Matrix4().compose(new THREE.Vector3(rec.px, rec.py, rec.pz), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), rec.yaw), new THREE.Vector3(rec.scale, rec.scale, rec.scale));
    const oldGids = [...rec.gids];
    const oldKey = `${rec.key}#${rec.lod}`;
    rec.gids.length = 0; rec.parts.length = 0;
    // The incoming level is born at fade 0 (invisible); the crossfade starts
    // once its batches are RESIDENT (S1) and retires the outgoing at the end (S5).
    for (const part of collectMeshes(lm.object)) {
      const m4 = new THREE.Matrix4().multiplyMatrices(place, part.local);
      const g = inst.add(part.geometry, part.material, m4, 0);
      if (g >= 0) { rec.gids.push(g); rec.parts.push({ geometry: part.geometry, material: part.material, matrix: m4 }); }
    }
    // L9: the upload the swap's batches will make resident (S1 planner bytes), plus the load's own metrics.
    const res = collectResources(lm.object);
    const uploadBytes = res.reduce((n, r) => n + r.bytes, 0);
    const now = performance.now();
    const siteId = [...this.built].find(([, b]) => b === build)?.[0] ?? -1;
    const fade = { inGids: [...rec.gids], outGids: oldGids, ready: false, started: 0, ledger: {
      key: rec.key, site: siteId, level: target, metrics: lm.metrics ?? { source: 'cache' as const, wireBytes: 0, fetchMs: 0, parseMs: 0 },
      queueMs: rec.candSince > 0 && lm.loadStartedAt ? Math.max(0, lm.loadStartedAt - rec.candSince) : 0,
      loadMs: lm.loadStartedAt && lm.loadedAt ? Math.max(0, lm.loadedAt - lm.loadStartedAt) : 0,
      uploadBytes, uploadTasks: Math.max(1, Math.ceil(uploadBytes / (3 * 1024 * 1024))), swapAt: now,
    } };
    rec.candSince = 0;
    build.fades.push(fade);
    const pend = rec.gids.map((g) => warmPending(inst.batchOf(g))).filter((p): p is Promise<void> => !!p);
    if (pend.length === 0) fade.ready = true;
    else void Promise.all(pend).then(() => { fade.ready = true; });
    rec.lod = target;
    rec.lastSwapAt = performance.now();
    lm.users++; lm.idleSince = undefined;
    const old = build.lodMasters.get(oldKey);
    if (old) { old.users--; if (old.users <= 0) old.idleSince = performance.now(); } // released after the grace (refineLods)
    this.refineStats.swaps++;
  }

  // ── R10: prefab catalog cache ──
  private prefabInfo: { map: Map<string, bigint>; n: number } | null = null;
  private prefabCatalog(conn: NonNullable<typeof atlas.conn>): Map<string, bigint> {
    const t = conn.db.prefabModel as unknown as { count?: () => number; iter: () => Iterable<{ prefabKey: string; assetId: bigint }> };
    const n = typeof t.count === 'function' ? t.count() : [...t.iter()].length;
    if (this.prefabInfo && this.prefabInfo.n === n) return this.prefabInfo.map;
    const map = new Map<string, bigint>();
    for (const pm of t.iter()) map.set(pm.prefabKey, pm.assetId);
    this.prefabInfo = { map, n };
    return map;
  }

  // ── Settlement asset warm ring ───────────────────────────────────────────────
  /** Warm-held asset refs per site — release closures matched to the cache key
   *  each load actually acquired (loadVariant falls back to the CANONICAL when
   *  no variant row exists; a blind disposeVariant then released NOTHING and
   *  the warm ref leaked forever). */
  private readonly warmHeld = new Map<number, { releases: Array<() => void> }>();
  private readonly warmingSites = new Set<number>();

  private async warmSettlement(site: SettlementSite): Promise<void> {
    this.warmingSites.add(site.id);
    try {
      const conn = atlas.conn;
      if (!conn) return;
      // Prime the baked-plan memo (also read by stampedHeight) — a ~300 KB JSON
      // fetch that otherwise lands inside buildSettlement's critical path.
      void this.planFromStatic(site, this.seed);
      // This site's civilization pack only (structure:civa_* / civb_*) — the
      // shared streetscape props are cache-warm after the first settlement.
      const prefix = `${CIV_PREFIX}${civilizationOf(site.siteSeed)}_`;
      const ids: bigint[] = [];
      for (const pm of conn.db.prefabModel.iter()) if (pm.prefabKey.startsWith(prefix)) ids.push(pm.assetId);
      if (ids.length === 0) return; // catalog not streamed yet — the ring retries next tick
      const held: { releases: Array<() => void> } = { releases: [] };
      this.warmHeld.set(site.id, held);
      const queue = [...ids];
      // 2 lanes (build itself uses 3): warming yields to any concurrent build.
      await Promise.all(Array.from({ length: 2 }, async () => {
        for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
          if (!this.warmHeld.has(site.id)) return; // released mid-warm — stop
          // Warm the representation the next build will actually request:
          // beyond the stream radius every assembly picks LOD 1 (and potato
          // pins it) — the release closure matches the acquired key.
          try {
            const { object: obj, release } = await this.assets.loadRepresentation(id, 1);
            if (!this.warmHeld.has(site.id)) { release(); return; }
            held.releases.push(release);
            if (this.appRef) queueTextureWarm(this.appRef, obj); // KTX2 pre-upload off the arrival path
          } catch { /* best-effort — the build path retries with its own timeout */ }
        }
      }));
    } finally { this.warmingSites.delete(site.id); }
  }

  private releaseWarm(siteId: number): void {
    const held = this.warmHeld.get(siteId);
    if (!held) return;
    this.warmHeld.delete(siteId); // in-flight warm lanes see this and self-clean
    for (const rel of held.releases) rel();
  }

  /** Build one settlement: load its prefabs (ONE await barrier), then assemble synchronously. */
  private async buildSettlement(site: SettlementSite): Promise<void> {
    if (this.built.has(site.id) || this.building.has(site.id)) return;
    this.building.add(site.id);
    this.buildStage.set(site.id, 'catalog');
    performance.mark(`settle:${site.id}:catalog`);
    const conn = atlas.conn;
    const seed = this.seed;
    if (!conn || !this.instancer || !this.roadMat || !this.sidewalkMat) { this.building.delete(site.id); this.buildStage.delete(site.id); return; }

    // prefabKey → assetId from the catalog (defaultScale is ignored — we fit-scale
    // instead). R10: cached module-wide, rebuilt only when the row count moves
    // (the ~400-row iteration ran at every build).
    const info = this.prefabCatalog(conn);
    // The prefab catalog may not have streamed in yet — bail (without committing) so the
    // next tick retries, rather than building a permanent roads-only settlement.
    if (info.size === 0) { this.building.delete(site.id); this.buildStage.delete(site.id); return; }

    this.buildStage.set(site.id, 'plan');
    performance.mark(`settle:${site.id}:plan`);
    const planKey = `${seed}:${site.id}`;
    let plan = this.planCache.get(planKey);
    if (!plan) {
      // Worker first (off-thread CityGenerator); sync generation only as the
      // no-Worker fallback — on the main thread it IS the traversal hitch.
      // Baked-plan fast path (item 3, 2026-07-20): city/town plans are baked to
      // static JSON at cook time — a fetch replaces 1-13 s of CityGenerator per
      // client. 404 (hamlet / stale seed) falls through to the worker as before.
      plan = (await this.planFromStatic(site, seed)) ?? (await this.planFromWorker(site, seed)) ?? undefined;
      if (!plan) {
        // Last-resort sync fallback — dynamic import keeps CityGenerator (the
        // whole engine city subsystem) out of the main chunk (perf sweep).
        const gen = await import('../lib/settlement_plan_gen.js');
        plan = gen.generateSettlementPlan(site, seed);
      }
      this.planCache.set(planKey, plan);
    }

    this.buildStage.set(site.id, 'resolve');
    performance.mark(`settle:${site.id}:resolve`);
    // ── Resolve every placement (key + world transform) up front ──
    interface Placement { key: string; wx: number; wz: number; yaw: number; collide: boolean; kind: 'building' | 'landmark' | 'prop'; pw?: number; pd?: number; padH?: number; tH?: number; fixedScale?: number; targetH?: number; fn?: string }
    const placements: Placement[] = [];
    // Buildings — one GLB per pad; collect, then sort biggest-multiplier-first so the
    // prominent civic buildings claim their space before the non-overlap filter (build loop).
    const buildingPlacements: Placement[] = [];
    // Buildable-terrain gate: reject pads whose RAW ground (pre-stamp) is too
    // steep — a flattened pad on a hillside reads as a building rammed into a
    // cliff. Samples the relaxed height field at the pad's reach.
    const MAX_BUILD_SLOPE = 0.35;
    const buildable = (wx: number, wz: number, r: number): boolean => {
      const h0 = terrainHeight(seed, wx, wz);
      let lo = h0, hi = h0;
      for (const [ox, oz] of [[r, 0], [-r, 0], [0, r], [0, -r]] as const) {
        const h = terrainHeight(seed, wx + ox, wz + oz);
        if (h < lo) lo = h;
        if (h > hi) hi = h;
      }
      return (hi - lo) / (2 * r) <= MAX_BUILD_SLOPE;
    };
    // Time-slice the resolution loops: ~5 raw terrainHeight evals per pad over
    // a city's ~380 pads is a ~30 ms synchronous burst — a long-task hitch at
    // stream-in. Yield to the frame every 64 pads and bail if the settlement
    // was torn down (world exit) during a yield.
    let sliceBudget = 64;
    const yieldSlice = async (): Promise<boolean> => {
      if (--sliceBudget > 0) return true;
      sliceBudget = 64;
      // scheduler.yield() (R4): resumes with priority instead of queueing
      // behind every pending timer/message of a streaming city.
      await yieldToMain();
      return this.building.has(site.id);
    };
    // ONE civilization per settlement (deterministic from siteSeed) — every
    // building + landmark below draws from that pack only; never mixed.
    const civ = civilizationOf(site.siteSeed);
    for (const pad of plan.buildings) {
      if (!(await yieldSlice())) return;
      if (pad.program.width < MIN_PAD_M || pad.program.depth < MIN_PAD_M) continue; // skip tiny pads
      const f = pad.footprint;
      const cx = (f[0] + f[2] + f[4] + f[6]) / 4;
      const cz = (f[1] + f[3] + f[5] + f[7]) / 4;
      if (!buildable(site.centerX + cx, site.centerZ + cz, Math.max(pad.program.width, pad.program.depth) * 0.6)) continue;
      const key0 = pickStructureForFunction(civ, site.biome, pad.program.function, pad.program.type, h01(site.siteSeed, pad.parcelId));
      // Radial skyline band (civ real-meter models): tall core → low rim.
      // The LANDMARK VIEW CORRIDOR (Unbuilt Town wave): heights inside the
      // approach wedge from the main entry are capped so the crown reads from
      // the arrival road — the "weenie" sightline rule.
      const distFrac = Math.hypot(cx, cz) / Math.max(1, plan.config.radius);
      const tH = key0.startsWith(CIV_PREFIX)
        ? targetHeightFor(site, seed, cx, cz, distFrac, h01(site.siteSeed, pad.parcelId * 3 + 1))
          * corridorHeightCap(plan, site.siteSeed, cx, cz)
        : undefined;
      // Downtown casting (monumental wave): a core pad that drew a wide named
      // complex would be lattice-crushed to a stump — re-cast it as a tower.
      const key = tH !== undefined && tH >= 150 ? preferTower(civ, key0, h01(site.siteSeed, pad.parcelId * 7 + 3)) : key0;
      buildingPlacements.push({ key, wx: site.centerX + cx, wz: site.centerZ + cz, yaw: pad.orientation, collide: true, kind: 'building', pw: pad.program.width, pd: pad.program.depth, padH: pad.padHeight, tH, fn: pad.program.function as string | undefined });
    }
    // Biggest presence first, so towers/civic anchors claim their space before
    // the non-overlap filter (civ target heights and legacy multipliers rank
    // on comparable "importance" scales).
    buildingPlacements.sort((a, b) => (b.tH ?? (BUILDING_SCALE_MULT[b.key] ?? 1)) - (a.tH ?? (BUILDING_SCALE_MULT[a.key] ?? 1)));
    // The CROWN (silhouette apex): one tower nearest the core reads ~20% over
    // the band — every skyline needs a single unambiguous top (Big-Medium-Small).
    if (site.tier === 'city') { // a district's apex is its program cluster (targetHeightFor) — the capital has ONE skyline
      let crown: Placement | null = null;
      let crownD = Infinity;
      for (const b of buildingPlacements) {
        if (b.tH === undefined) continue;
        const d = Math.hypot(b.wx - site.centerX, b.wz - site.centerZ);
        if (d < crownD) { crownD = d; crown = b; }
      }
      if (crown && crown.tH !== undefined) crown.tH = Math.min(560, crown.tH * 1.2);
    }
    // Civic landmarks place FIRST (before the tower sort): they are the tallest
    // anchors of the skyline and at monumental footprints they'd otherwise be
    // crowded out by whichever towers assembled first.
    let landmarks = 0;
    // A capital's plan is EMPTY until its skeleton lands (phase 2 places the civic
    // pads); the ring fallback must not stand a lone 520 m landmark on it.
    const landmarkCap = site.tier === 'capital' ? 4 : site.tier === 'city' || site.tier === 'district' ? 5 : site.tier === 'town' || site.tier === 'circuit' ? 2 : 1;
    for (const pc of plan.parcels) {
      if (landmarks >= landmarkCap) break;
      if (pc.zone !== 'park') continue;
      if (!(await yieldSlice())) return;
      const [cx, cz] = polyCentroid(pc.polygon);
      // Sitte: monuments stand OFF-CENTER, read against an edge — never
      // marooned in the middle of the square. Push the first (core) landmark
      // ~55% toward its parcel rim in a seed-stable direction.
      let lx = cx, lz = cz;
      if (landmarks === 0) {
        let pr = 0;
        for (let vi = 0; vi < pc.polygon.length; vi += 2) {
          pr = Math.max(pr, Math.hypot(pc.polygon[vi] - cx, pc.polygon[vi + 1] - cz));
        }
        const ang = h01(site.siteSeed, 777) * Math.PI * 2;
        lx += Math.cos(ang) * Math.min(18, pr * 0.55);
        lz += Math.sin(ang) * Math.min(18, pr * 0.55);
      }
      if (!buildable(site.centerX + lx, site.centerZ + lz, 8)) continue;
      const key = pickLandmark(civ, h01(site.siteSeed, pc.id));
      placements.push({ key, wx: site.centerX + lx, wz: site.centerZ + lz, yaw: h01(site.siteSeed, pc.id + 7) * Math.PI * 2, collide: true, kind: 'landmark' });
      landmarks++;
    }
    // Park-less archetypes (the dense lattice zones no parks): the skyline
    // still needs its civic anchors — ring them off-center around the core
    // (the overlap filter spaces them against the towers).
    if (landmarks === 0) {
      const baseAng = h01(site.siteSeed, 771) * Math.PI * 2;
      for (let li = 0; li < landmarkCap * 3 && landmarks < landmarkCap; li++) {
        const ang = baseAng + li * 2.399963;
        const rr = 26 + (li % 3) * 22;
        const lx = Math.cos(ang) * rr, lz = Math.sin(ang) * rr;
        if (!buildable(site.centerX + lx, site.centerZ + lz, 8)) continue;
        const key = pickLandmark(civ, h01(site.siteSeed, 780 + li));
        placements.push({ key, wx: site.centerX + lx, wz: site.centerZ + lz, yaw: h01(site.siteSeed, 790 + li) * Math.PI * 2, collide: true, kind: 'landmark' });
        landmarks++;
      }
    }
    // ── SWG ports (space wave 2026-07-24) ──
    // Cities host a SPACEPORT (the landed spacer ship is the terminal —
    // pack-independent infrastructure, like street furniture); cities AND
    // towns host a SHUTTLE PAD. Spots are deterministic (lib/ports) and match
    // AtlasTravelModule's prompts + the server's proximity checks. Ports are
    // pushed BEFORE buildings so the overlap filter clears ground around them.
    const sport = spaceportSpot(seed, site);
    // The sky-ship is the civ-A city hall — it flies over the cities that
    // used that model (the user's law: "all the cities that use that glb").
    if (sport && civ === 'a' && info.has(SKY_SHIP_PREFAB)) {
      // THE SKY-SHIP = civa_city_hall (the user's Ostorch giant, measured
      // 72.8×21.2×90 m raw): as a 340 m-band civ LANDMARK it scaled ×16 to a
      // 1.4 km slab lying across the city. It is no longer fabric
      // (biome_settlements) — the same hall, at grandeur (fixedScale 13 →
      // 1.17 km long, 276 m tall), now hangs 760 m up as the boardable
      // sky-ship. The civa_ship liner added in its place is retired.
      placements.push({ key: SKY_SHIP_PREFAB, wx: site.centerX + sport.x, wz: site.centerZ + sport.z, yaw: sport.yaw, collide: true, kind: 'landmark', fixedScale: 13.0 });
      // The BOARDING HALL (sky-ship wave): with the ship holding station
      // 760 m up, the pad gets a terminal building — the E-board prompt
      // (SkyShipModule) lifts you from here into the hull above.
      if (info.has('structure:spaceport_terminal')) {
        placements.push({
          key: 'structure:spaceport_terminal',
          wx: site.centerX + sport.x + Math.cos(sport.yaw) * 14,
          wz: site.centerZ + sport.z + Math.sin(sport.yaw) * 14,
          yaw: sport.yaw + Math.PI / 2, collide: true, kind: 'landmark', fixedScale: 6,
        });
      }
    }
    const shut = shuttleSpot(seed, site);
    if (shut && info.has('structure:landing_pad_structure')) {
      // collide:true (high-relief audit): with no collider players walked
      // THROUGH the platform; arrivals land 13 m beside it, so a solid pad
      // can no longer trap a spawn.
      placements.push({ key: 'structure:landing_pad_structure', wx: site.centerX + shut.x, wz: site.centerZ + shut.z, yaw: shut.yaw, collide: true, kind: 'landmark', fixedScale: 9 });
    }
    const term = terminalSpot(seed, site);
    if (term && info.has('prop:mission_terminal')) {
      // collide:true: an interactable kiosk you could stand inside reads broken.
      placements.push({ key: 'prop:mission_terminal', wx: site.centerX + term.x, wz: site.centerZ + term.z, yaw: term.yaw, collide: true, kind: 'prop', fixedScale: 1.6 });
    }
    placements.push(...buildingPlacements);
    // ── Street furniture: the CIVIC FURNISHING PLANNER (street_furniture.ts,
    // 2026-08-31 wave). Urban-design rules — the furnishing zone, staggered
    // arterial lamps at class spacing, Whyte seating (plaza ring +
    // conversation pairs + street-watching benches), corner bollard/kiosk
    // clusters, frontage stalls/racks — with FULL validation: outside every
    // pad polygon, off every carriageway, clear of venue stages/the notice
    // board, min-spaced, slope-checked. The per-placement loop below
    // re-validates against the ACTUAL scaled building footprints (fit-scale
    // and the shrink ladder can outgrow the plan's pads). The engine's
    // 3-rule left-side scatter (plan.streetProps) is retired; solid seats,
    // kiosks and stalls now carry walk colliders.
    {
      const slopeAt = (lx: number, lz: number): number => {
        const wx = site.centerX + lx, wz = site.centerZ + lz;
        const r = 2;
        const dhx = Math.abs(terrainHeight(seed, wx + r, wz) - terrainHeight(seed, wx - r, wz));
        const dhz = Math.abs(terrainHeight(seed, wx, wz + r) - terrainHeight(seed, wx, wz - r));
        return Math.max(dhx, dhz) / (2 * r);
      };
      for (const fp of planStreetFurniture(plan, site, seed, slopeAt)) {
        if (!info.has(fp.key)) continue; // partial catalog self-heals
        placements.push({
          key: fp.key, wx: site.centerX + fp.x, wz: site.centerZ + fp.z, yaw: fp.yaw,
          collide: fp.solid, kind: 'prop', targetH: fp.targetH,
        });
      }
    }

    // ── Collider proxies for colliding keys (cooked trimesh/hull JSON) ──
    // Fetched once per key for the session, in parallel with the GLB loads
    // below (tiny JSONs). A missing row / failed fetch caches null → the
    // assembly falls back to the pivot-corrected bbox box.
    const proxyP = Promise.all(
      [...new Set(placements.filter((p) => p.collide).map((p) => p.key))]
        .filter((k) => !this.proxies.has(k) && info.has(k))
        .map(async (k) => {
          try {
            const col = conn.db.assetCollider.assetId.find(info.get(k)!);
            if (!col) { this.proxies.set(k, null); return; }
            const p = (await (await fetch(`${ASSET_STORE_BASE}/${col.colliderUri}`)).json()) as ColliderProxy;
            this.proxies.set(k, p.positions?.length || p.points?.length || p.halfExtents ? p : null);
          } catch { this.proxies.set(k, null); }
        }),
    );

    // ── Load each unique prefab once, BOUNDED CONCURRENCY (3 lanes) ──
    // Promise.all over ~30-50 city GLBs was a thundering herd: every fetch's
    // parse+smooth completion crowded the same frames. Three lanes keep the
    // pipe full while letting frames breathe between completions (profiled
    // 2026-07-20: load-path work was the dominant area-entry cost).
    const uniqueKeys = [...new Set(placements.map((p) => p.key))].filter((k) => info.has(k));
    const masters = new Map<string, THREE.Object3D>();
    const releases: Array<() => void> = [];
    // forceLowLod (potato): always the simplified variants — see tick()'s WHY.
    // Boot-first-build law (F16): under the loading gate, build at LOD1
    // regardless of distance. The boot "Structures 0/1" stall (~45-70 s on
    // real hardware, warm cache, 7 reproductions) WAS the full-detail LOD0 GLB
    // set loading for the city the player spawns INSIDE — the warm ring only
    // helps sites you APPROACH. The LOD1 city satisfies the gate several-fold
    // sooner; tick()'s swap logic upgrades to LOD0 in the background after the
    // gate drops (dispose→rebuild from a warm cache, time-sliced).
    // LOD1-FIRST, always (R8, 2026-09-02): the pack-served LOD1 set assembles
    // in seconds; the per-building refinement then brings LOD0 to the
    // buildings that fill the screen, biggest first, with texture-capped
    // files — the whole-city LOD0 build (40 × 18–21 MB) was the "takes a
    // while from LOD1 to LOD0". builtLod now means the COLLIDER tier
    // (1 = boxes, 0 = trimesh hulls, upgraded in place inside `near`).
    const lodPick: 0 | 1 = 1;
    this.builtLod.set(site.id, lodPick);
    const loadQueue = [...uniqueKeys];
    let loadedN = 0;
    this.buildStage.set(site.id, `load:0/${uniqueKeys.length}`);
    performance.mark(`settle:${site.id}:load`);
    // Per-load timeout: a saturated decode pipeline (hundreds of texture jobs
    // at area entry) can trickle for minutes — without a bound, ONE slow
    // prefab parks the whole site in `building` forever with no retry. A
    // timed-out prefab is skipped this pass; the settlement still assembles
    // with what arrived, and the asset stays cache-warm for the next site.
    // Probe mode: swiftshader's decode trickle regularly blows the 120 s
    // per-load budget — headless verification needs the loads to LAND, not to
    // be fast. Real clients keep the tight budget (self-heals next pass).
    const LOAD_TIMEOUT_MS = PROBE_MODE ? 600_000 : 120_000;
    await Promise.all(Array.from({ length: Math.min(3, loadQueue.length) }, async () => {
      for (let key = loadQueue.shift(); key !== undefined; key = loadQueue.shift()) {
        const assetId = info.get(key)!;
        try {
          // Civ structures follow the settlement's LOD; props/decor (R2) take
          // LOD1 whenever the settlement is LOD1 OR the tier's propLod says so
          // (a 5 MB LOD0 lamppost was loading at every tier).
          const propWant = isPropKey(key) ? Math.max(propLod(activePreset()), lodPick === 1 ? 1 : 0) : 0; // props/decor: the tier's LOD (0/1/2), at least LOD1 in a LOD1 settlement
          const wantV = (lodPick === 1 && key.startsWith(CIV_PREFIX)) || propWant > 0;
          // Minimal tier (2026-09-08): civ structures assemble at the tier's
          // settlementBuildLod (2 = 16 % of LOD0) instead of LOD1.
          const civLod = key.startsWith(CIV_PREFIX) && lodPick === 1 ? (activePreset().settlementBuildLod ?? 1) : 1;
          // loadRepresentation: LOD1 variant when the row exists, else canonical
          // — its release closure matches whichever cache key was acquired (a
          // blind disposeVariant on the fallback used to release NOTHING).
          const loadP = this.assets.loadRepresentation(assetId, wantV ? (isPropKey(key) ? propWant : civLod) : 0);
          const master = await Promise.race([
            loadP,
            new Promise<null>((r) => setTimeout(() => r(null), LOAD_TIMEOUT_MS)),
          ]);
          if (master) {
            masters.set(key, master.object);
            releases.push(master.release);
          } else {
            // Timed out: the in-flight load keeps running; when it lands,
            // release our implicit ref so it parks cache-warm (no leak).
            void loadP.then((r) => r.release()).catch(() => { /* failed load */ });
          }
        } catch { /* asset not registered / fetch failed — skip this prefab */ }
        this.buildStage.set(site.id, `load:${++loadedN}/${uniqueKeys.length}`);
      }
    }));

    await proxyP; // colliders resolve before assembly reads this.proxies

    // The player may have left the Commons or this site may have been evicted while we loaded.
    if (atlas.currentWorldId !== atlas.commonsWorldId || !this.building.has(site.id)) {
      for (const rel of releases) rel();
      this.building.delete(site.id);
      this.buildStage.delete(site.id);
      return;
    }

    this.buildStage.set(site.id, `warm:${masters.size}/${uniqueKeys.length}`);
    performance.mark(`settle:${site.id}:warm`);
    // ── Shader-pipeline pre-warm ──
    // First render of each new (geometry, material) batch compiles its program
    // SYNCHRONOUSLY — profiled as recurring 200-650 ms bursts exactly when the
    // player arrives. Ghost-instance every unique part far below the world,
    // let compileAsync (KHR_parallel_shader_compile / WebGPU async pipelines)
    // compile them off the critical path, then drop the ghosts. Best-effort:
    // without compileAsync the ghosts still pull the compiles forward into the
    // load window instead of the arrival frames.
    const ghostIds: number[] = [];
    if (this.instancer) {
      // Chunked pipeline warm (live-profiled on real hardware 2026-07-31: the
      // one-gulp whole-city compileAsync was a single ~2.7 s main-thread task —
      // the surviving city-arrival freeze). Ghost-add a few parts, compile just
      // their pending pipelines, and yield a frame between chunks so the warm
      // spreads across the approach instead of landing as one monolith.
      const ghost = new THREE.Matrix4().makeTranslation(0, -50000, 0);
      const parts: { geometry: THREE.BufferGeometry; material: THREE.Material }[] = [];
      for (const master of masters.values()) for (const part of collectMeshes(master)) parts.push(part);
      const r = this.appRef?.renderer as unknown as {
        native?: {
          compileAsync?: (scene: unknown, camera: unknown, targetScene?: unknown) => Promise<unknown>;
          initTexture?: (t: THREE.Texture) => void;
        };
        cameras?: { main?: unknown };
      } | undefined;
      // Eager per-batch texture upload: queueTextureWarm's 5 ms/32 ms drain
      // loses the race against the ghost warm's first-bind renders — the
      // 350-540 ms gate-up "compile" slivers were KTX2 mip uploads paid BY THE
      // RENDER (live-profiled round 4). Uploading here rides the same pacing
      // as the compiles; the drain queue still handles the CPU-mip strip.
      const warmedTex = new Set<string>();
      const initBatchTextures = (m: THREE.Material): void => {
        for (const v of Object.values(m as unknown as Record<string, unknown>)) {
          const t = v as THREE.Texture | null | undefined;
          if (t && (t as { isTexture?: boolean }).isTexture && !warmedTex.has(t.uuid)) {
            warmedTex.add(t.uuid);
            try { r?.native?.initTexture?.(t); } catch { /* upload is best-effort */ }
          }
        }
      };
      // Per-BATCH compiles (live-profiled 2026-07-31): compileAsync(scene)
      // pays an O(scene) sweep PER CALL (~400-550 ms in a built world)
      // regardless of how few pipelines are pending — chunking the ghost adds
      // never helped because every chunk re-paid the sweep. Compile each
      // newborn batch mesh directly (targetScene keeps the real lights/env
      // context); the per-call cost collapses to that batch's node build.
      // Gate DOWN (player watching): a DOUBLE-rAF breath between compiles so
      // a frame actually PRESENTS (setTimeout(16) fired before the renderer
      // caught up). Gate UP: loading screen hides jank — no breath, finish
      // sooner.
      // ORDERING LAW (live-profiled round 3, 2026-07-31): the node build for a
      // ghost batch MUST happen before any await after its birth — compileAsync
      // builds node graphs in its SYNCHRONOUS section, so `add → compile` with
      // no yield in between means an interleaved render can never catch an
      // uncompiled batch (chunk-then-compile left batches 2..N raw across the
      // awaits, and the RENDER paid their builds as 250-500 ms slivers — plus
      // one 3.2 s monolith when many were pending).
      const warmedBatches = new Set<THREE.Object3D>();
      let sinceYield = 0;
      for (const part of parts) {
        const gid = this.instancer.add(part.geometry, part.material, ghost);
        if (gid >= 0) ghostIds.push(gid);
        const bm = this.instancer.meshFor(part.geometry, part.material);
        let yielded = false;
        if (bm && !warmedBatches.has(bm)) {
          warmedBatches.add(bm);
          initBatchTextures(part.material);
          try {
            if (r?.native?.compileAsync && r.cameras?.main && this.scene) {
              await r.native.compileAsync(bm, r.cameras.main, this.scene);
            }
          } catch { /* warm is best-effort */ }
          if (!atlas.loadingGate) {
            // A frame must PRESENT between compiles while the player watches.
            // XR LAW (2026-08-16, caught live by [XRSettle] pinned at
            // warm:28/28): during an immersive session the page's WINDOW
            // requestAnimationFrame does not tick — the session owns the
            // frame loop — so this breath never resolved and city assembly
            // parked forever inside the warm loop ("structures never load
            // in — VR only"). Breathe on the SESSION's rAF while presenting.
            await new Promise<void>((res) => {
              const xr = (this.appRef?.renderer.native as unknown as {
                xr?: { isPresenting?: boolean; getSession?: () => { requestAnimationFrame(cb: () => void): number } | null };
              })?.xr;
              const session = xr?.isPresenting ? xr.getSession?.() : null;
              if (session) session.requestAnimationFrame(() => session.requestAnimationFrame(() => res()));
              else requestAnimationFrame(() => requestAnimationFrame(() => res()));
            });
            yielded = true;
          }
          sinceYield++;
        }
        if (atlas.loadingGate && sinceYield >= 8) {
          sinceYield = 0;
          await yieldToMain(); // R4: prioritised continuation (scheduler.yield), not the back of the queue
          yielded = true;
        }
        // The world may have changed / the site may have been released during
        // a yield — clean up everything this build acquired and bail.
        if (yielded && (atlas.currentWorldId !== atlas.commonsWorldId || !this.building.has(site.id))) {
          for (const g of ghostIds) this.instancer.remove(g);
          for (const rel of releases) rel();
          this.building.delete(site.id);
          this.buildStage.delete(site.id);
          return;
        }
      }
      // Ghosts are NOT removed here. InstancedMeshPool DESTROYS a batch the
      // moment it empties — removing the ghosts before assembly threw away
      // every just-compiled render object, assembly re-created every batch,
      // and the debounced whole-scene warm re-built them all: the SECOND
      // ~2.7 s monolith in the live profile. finishAssembly removes them once
      // real instances share the batches.
    }

    // ── Time-sliced assembly (movement-freeze fix): a 72-building city placed
    // synchronously (instancing + colliders + labels in ONE frame) froze the
    // frame for up to a second at the 350 m stream-in ring. Placements now
    // assemble a few per frame from tick(); colliders are cheap since
    // PhysicsModule no longer steps the world per add.
    this.buildStage.set(site.id, 'assemble');
    performance.mark(`settle:${site.id}:assemble`);
    await this.ensureRoadMats(); // street-surface materials (once per session)
    this.assemblyQueue.push({
      site, seed, plan, masters, placements,
      info,
      build: { instanceIds: [], ghostIds, roadGroup: new THREE.Group(), colliderIds: [], enterableIds: [], releases, labelData: [], labelSprites: [], dressingGroups: [], seatIds: [], rotors: [], lodBuildings: [], occluders: new SpatialHash2D<Occluder>(48), lodMasters: new Map(), fades: [], colliders: [] },
      tOverlap: 0, tCollider: 0, collidersNow: 0, collidersDeferred: 0,
      stamps: [],
      placed: new SpatialHash2D<Placed>(48), dressAnchors: [], lampAnchors: [], ci: 0, i: 0,
      placedN: 0, ladderN: 0, skipN: 0, noMasterN: 0,
    });
  }

  /** Assemble a bounded number of queued placements this frame (called every tick). */
  private assembleTick(budget = PROBE_MODE ? 64 : 8): void {
    let left = budget;
    // Wall-clock cap on top of the placement count: 8 placements are usually
    // cheap, but a run of trimesh-collider bakes can put 8 × multi-ms BVH
    // builds in ONE frame (sweep-confirmed) — stop when the slice is spent.
    const t0 = performance.now();
    const MS_BUDGET = PROBE_MODE ? 50 : 6;
    while (left > 0 && this.assemblyQueue.length > 0 && performance.now() - t0 < MS_BUDGET) {
      const job = this.assemblyQueue[0];
      while (left > 0 && job.i < job.placements.length && performance.now() - t0 < MS_BUDGET) {
        this.assembleOne(job, job.placements[job.i]);
        job.i++;
        left--;
      }
      if (job.i >= job.placements.length) {
        this.assemblyQueue.shift();
        this.finishAssembly(job);
      }
    }
  }

  /** Abort every in-flight assembly (world exit): undo partial placements. */
  private abortAssemblies(): void {
    for (const job of this.assemblyQueue) {
      for (const id of job.build.instanceIds) this.instancer?.remove(id);
      for (const gid of job.build.ghostIds) this.instancer?.remove(gid);
      for (const eid of job.build.enterableIds) enterables.delete(eid);
      for (const cid of job.build.colliderIds) this.physics.removeStaticCollider(cid);
      for (const c of job.build.colliders) this.ringRemove(c);
      for (const rel of job.build.releases) rel();
      this.removeShip(job.site.id);          // a placed hull and the site's solids are not in the undo lists above (2026-09-12)
      clearStructureSolids(job.site.id);
      this.building.delete(job.site.id);
      this.buildStage.delete(job.site.id);
    }
    this.assemblyQueue.length = 0;
  }

  // Shared scratch for assembleOne (single-threaded, reused across jobs).
  private readonly _placeMat = new THREE.Matrix4();
  private readonly _instMat = new THREE.Matrix4();
  private readonly _q = new THREE.Quaternion();
  private readonly _up = new THREE.Vector3(0, 1, 0);
  private readonly _pos = new THREE.Vector3();
  private readonly _scl = new THREE.Vector3();

  /** Place ONE resolved placement (the body of the old synchronous loop). */
  private assembleOne(job: AssemblyJob, pl: AssemblyJob['placements'][number]): void {
    const { site, seed, masters, build, placed, lampAnchors } = job;
    const placeMat = this._placeMat;
    const instMat = this._instMat;
    const q = this._q;
    const up = this._up;
    const pos = this._pos;
    const scl = this._scl;
    let ci = job.ci;

      const master = masters.get(pl.key);
      if (!master) { if (pl.kind !== 'prop') job.noMasterN++; job.ci = ci; return; }
      // Size to fit. LEGACY (normalized ~1.9 m) GLBs scale to their pad
      // footprint with the historical clamps; CIV (real-meter) GLBs scale to
      // the radial skyline HEIGHT band, softly capped by the pad so a tower
      // never swallows its street. Landmarks: civ models scale to a per-tier
      // presence height; legacy keep the fixed scale.
      let scale: number;
      let scaledH = 0;
      if (pl.fixedScale !== undefined) {
        // Port/terminal infrastructure places at a HAND scale — the civ
        // height bands, lattice caps, and prop scale do not apply to it.
        const bb = this.bboxFor(pl.key, master);
        scale = pl.fixedScale;
        scaledH = Math.max(0.2, bb.max.y - bb.min.y) * scale;
      } else if (pl.kind === 'building') {
        const bb = this.bboxFor(pl.key, master);
        const glbW = Math.max(0.2, bb.max.x - bb.min.x);
        const glbD = Math.max(0.2, bb.max.z - bb.min.z);
        const glbH = Math.max(0.2, bb.max.y - bb.min.y);
        const fit = BUILD_FILL * Math.min((pl.pw ?? 12) / glbW, (pl.pd ?? 12) / glbD);
        if (pl.tH !== undefined) {
          scale = Math.max(CIV_SCALE_MIN, Math.min(CIV_SCALE_MAX, pl.tH / glbH));
          // Lattice cap (×10 wave): a building may fill its whole block
          // wall-to-wall but never the street — replaces the pad-fit crush.
          scale = Math.min(scale, Math.max(0.12, CIV_LOT_W[job.site.tier] / Math.max(glbW, glbD)));
        } else {
          const baseFit = Math.max(BUILD_MIN_SCALE, Math.min(BUILD_MAX_SCALE, fit));
          scale = Math.min(BUILD_SCALE_CAP, baseFit * (BUILDING_SCALE_MULT[pl.key] ?? 1)); // per-prefab size correction
        }
        scaledH = glbH * scale;
      } else if (pl.kind === 'landmark') {
        const bb = this.bboxFor(pl.key, master);
        const glbH = Math.max(0.2, bb.max.y - bb.min.y);
        const glbWD = Math.max(0.2, bb.max.x - bb.min.x, bb.max.z - bb.min.z);
        scale = pl.key.startsWith(CIV_PREFIX)
          ? Math.min(CIV_LANDMARK_H[job.site.tier] / glbH, CIV_LANDMARK_LOT_W[job.site.tier] / glbWD) // height band, footprint-capped
          : LANDMARK_SCALE;
        scaledH = glbH * scale;
      } else if (pl.targetH !== undefined) {
        // Planner furniture: scale the GLB to its real-world silhouette
        // (a bench is 0.95 m, a lamp 5.6 m — the flat ×2.5 made sofa-sized
        // benches and knee-high lamps depending on the cook's normalization).
        const bb = this.bboxFor(pl.key, master);
        const glbH = Math.max(0.2, bb.max.y - bb.min.y);
        scale = Math.max(0.2, Math.min(8, pl.targetH / glbH));
        scaledH = glbH * scale;
      } else {
        scale = PROP_SCALE;
      }
      // Size-aware non-overlap: skip a building/landmark that would mesh into one already
      // placed (its world footprint + a gap). Props (street furniture) are exempt.
      // The gap is HEIGHT-AWARE for civ models — towers keep tower distance,
      // low-rise packs tight (real cities: taller ⇒ farther apart).
      let footR = 0.6; // props: small footprint
      if (pl.kind === 'prop' && pl.targetH !== undefined) {
        // Planner furniture re-validates against the ACTUAL placed footprints:
        // fit-scale + the civ shrink ladder can outgrow the plan's pad
        // polygons the planner checked against. A blocked piece is dropped —
        // furniture never pokes through a wall.
        const tq0 = performance.now();
        const hit = placed.anyWithin(pl.wx, pl.wz, 0.7, (p) => Math.hypot(p.x - pl.wx, p.z - pl.wz) < p.r + 0.7);
        job.tOverlap += performance.now() - tq0;
        if (hit) {
          job.skipN++;
          return;
        }
      }
      if (pl.kind !== 'prop') {
        const fb = this.bboxFor(pl.key, master);
        const glbHf = Math.max(0.2, fb.max.y - fb.min.y);
        const footHalf = Math.max(fb.max.x - fb.min.x, fb.max.z - fb.min.z) / 2;
        // Blocking-radius cap: the cooked civa_ship GLB measures >1700 m raw
        // (far-flung cooked verts), so at face value its footprint radius
        // (1164 m) out-blocked the WHOLE ~420 m city plan — every civ-A
        // spaceport city assembled 1 placed / 350 skipped (funnel diag
        // 2026-08-13). `placed` exists for building-vs-building spacing and no
        // legitimate footprint exceeds a city block, so cap what any single
        // placement may BLOCK at 60 m; render and colliders stay untouched.
        let fr = Math.min(footHalf * scale, 60);
        const gap = pl.key.startsWith(CIV_PREFIX) ? civGapFor(scaledH) : BUILD_GAP_M;
        const tq1 = performance.now();
        let blocked = placed.anyWithin(pl.wx, pl.wz, fr + gap, (p) => Math.hypot(p.x - pl.wx, p.z - pl.wz) < p.r + fr + gap);
        if (blocked && pl.key.startsWith(CIV_PREFIX)) {
          // Shrink ladder (monumental wave): at monumental footprints the
          // binary reject emptied whole towns (8 buildings of ~130) — the
          // density-wave plans pack ROWHOUSE lots 9–16 m apart, so anything
          // wide excludes its whole block. Step the WHOLE building down
          // (footprint AND height) until the pad fits: giants keep their
          // pocket, mids ring them, rowhouse-scale infill re-fills the block —
          // the Big-Medium-Small pyramid. Floors: 12 m height, 5 m width
          // (a needle stepped thinner reads as a pole).
          for (const f of [0.72, 0.5, 0.34, 0.22, 0.15]) {
            const s2 = scale * f;
            if (glbHf * s2 < 12 || footHalf * 2 * s2 < 5) break;
            const fr2 = footHalf * s2;
            const g2 = civGapFor(glbHf * s2);
            if (!placed.anyWithin(pl.wx, pl.wz, fr2 + g2, (p) => Math.hypot(p.x - pl.wx, p.z - pl.wz) < p.r + fr2 + g2)) {
              scale = s2; scaledH = glbHf * s2; fr = fr2; blocked = false;
              job.ladderN++;
              break;
            }
          }
        }
        if (blocked) {
          job.skipN++; job.ci = ci;
          const sk = intDiag[`__skip_${pl.key}`] ?? (intDiag[`__skip_${pl.key}`] = { n: 0, ok: 0, w: 0, h: 0, d: 0 });
          sk.n++; sk.w = Math.round(fr);
          return;
        }
        job.tOverlap += performance.now() - tq1;
        placed.insert({ x: pl.wx, z: pl.wz, r: fr });
        job.placedN++;
        { // diag: what actually placed, with its recorded blocking radius
          const pk = intDiag[`__placed_${pl.key}`] ?? (intDiag[`__placed_${pl.key}`] = { n: 0, ok: 0, w: 0, h: 0, d: 0 });
          pk.n++; pk.w = Math.round(fr); pk.h = Math.round(scaledH);
        }
        footR = Math.max(1, fr * 0.7);
      }
      // Footprint-aware seat: MIN terrain height across the footprint − a plinth sink,
      // so a building on a slope embeds its downhill edge instead of floating on it.
      // A building's ground was FLATTENED to its pad height by the terrain stamp —
      // seat exactly there (slightly embedded); everything else seats on the
      // stamped ground via terrainSeat (which samples stampedHeight).
      const wy = pl.padH !== undefined
        ? pl.padH - 0.15
        : terrainSeat(seed, pl.wx, pl.wz, footR, pl.kind === 'prop' ? 0.1 : 0.3).y;
      // Pivot compensation (collider wave 2026-07-22): the cooked civ GLBs are
      // OFF-PIVOT — bbox centers sit 40–1290 m from the model origin and civA
      // bases float 70–130 m up (the cooker normalized raw arrays without node
      // transforms). Place each instance so its measured bbox BOTTOM-CENTER
      // lands on the pad: visuals stand exactly where the plan says, and the
      // mesh-space collider proxy aligns by construction. A no-op (offsets ~0)
      // for well-pivoted legacy models and props.
      const pvb = this.bboxFor(pl.key, master);
      const pcx = ((pvb.min.x + pvb.max.x) / 2) * scale;
      const pcz = ((pvb.min.z + pvb.max.z) / 2) * scale;
      const pby = pvb.min.y * scale;
      const cosY = Math.cos(pl.yaw), sinY = Math.sin(pl.yaw);
      const px = pl.wx - (pcx * cosY + pcz * sinY);
      const py = wy - pby;
      const pz = pl.wz - (-pcx * sinY + pcz * cosY);
      q.setFromAxisAngle(up, pl.yaw);
      placeMat.compose(pos.set(px, py, pz), q, scl.set(scale, scale, scale));
      // The spaceport ship LIVES: it lifts off on a shared-clock schedule and
      // lands back on this exact spot, so it renders as a per-site animated
      // clone (one per city in range), never a frozen static instance.
      if (pl.key === SKY_SHIP_PREFAB) {
        this.placeShip(site, master, px, py, pz, pl.yaw, scale);
        job.ci = ci;
        return;
      }
      const instancer = this.instancer;
      if (!instancer) { job.ci = ci; return; }
      { // funnel diag (probe): count what reaches the instancing stage per kind
        const fk = `__reach_${pl.kind}${pl.collide ? '' : '_nocollide'}`;
        const fd = intDiag[fk] ?? (intDiag[fk] = { n: 0, ok: 0, w: 0, h: 0, d: 0 });
        fd.n++;
      }
      // Interior foundation: remember this building's OWN instances so an
      // eligible one can be hidden (and restored) when a player steps inside.
      const plGids: number[] = [];
      const plParts: Array<{ geometry: THREE.BufferGeometry; material: THREE.Material; matrix: THREE.Matrix4 }> = [];
      for (const part of collectMeshes(master)) {
        const m4 = instMat.multiplyMatrices(placeMat, part.local);
        const id = instancer.add(part.geometry, part.material, m4);
        if (id >= 0) { build.instanceIds.push(id); plGids.push(id); plParts.push({ geometry: part.geometry, material: part.material, matrix: m4.clone() }); }
      }
      // R8: the building's LOD record shares plGids/plParts with the enterable
      // closures below, so a later screen-space swap keeps hide/restore truthful.
      const slot = { gids: plGids, parts: plParts, hidden: false };
      if (pl.kind !== 'prop' && pl.key.startsWith(CIV_PREFIX)) {
        const lod0 = ((this.builtLod.get(site.id) ?? 1) === 1 ? (activePreset().settlementBuildLod ?? 1) : 0) as 0 | 1 | 2;
        const rec: BuildingLodRec = { key: pl.key, assetId: job.info.get(pl.key) ?? 0n, kind: pl.kind, px, py, pz, yaw: pl.yaw, scale, cx: pl.wx, cz: pl.wz, h: scaledH, lod: lod0, gids: plGids, parts: plParts, hidden: false, swapping: false, lastSwapAt: 0, candSince: 0, fr: footprintRadius((pvb.max.x - pvb.min.x) * scale, (pvb.max.z - pvb.min.z) * scale), occludedSince: 0, idx: build.lodBuildings.length };
        build.lodBuildings.push(rec);
        build.occluders.insert({ id: build.lodBuildings.length - 1, x: pl.wx, z: pl.wz, r: rec.fr, top: py + scaledH });
        const mk = `${pl.key}#${lod0}`;
        const lm = build.lodMasters.get(mk) ?? { object: master, release: () => { /* owned by build.releases */ }, users: 0 };
        lm.users++; build.lodMasters.set(mk, lm);
        Object.defineProperty(slot, 'hidden', { get: () => rec.hidden, set: (v: boolean) => { rec.hidden = v; } });
      }
      if (pl.collide) {
        const cid = `settle:${site.id}:${ci++}`;
        // Trimesh proxies only for NEAR (LOD0) builds: a city carries ~150+
        // colliding buildings at ~2–25k tris each — real Rapier BVH memory.
        // Far (LOD1) builds keep cheap boxes; crossing the LOD boundary
        // re-assembles the settlement, upgrading its colliders automatically.
        let proxy = this.builtLod.get(site.id) === 0 ? this.proxies.get(pl.key) : null;
        if (proxy && proxy.shape === 'trimesh_static' && proxy.positions && /_(scifi|city)\d/.test(pl.key)) {
          let hull = this.hulls.get(pl.key);
          if (!hull) {
            const src = proxy.positions;
            const n = (src.length / 3) | 0;
            const step = Math.max(1, Math.ceil(n / 96));
            const pts: number[] = [];
            for (let i = 0; i < n; i += step) pts.push(src[i * 3], src[i * 3 + 1], src[i * 3 + 2]);
            hull = { shape: 'convex_hull', points: pts };
            this.hulls.set(pl.key, hull);
          }
          proxy = hull;
        }
        // The collider this placement currently has (a box on the LOD1 build;
        // the in-place upgrade swaps it for the cooked hull and updates the
        // holder so an enterable's restore() re-adds the upgraded one).
        const colHolder: { proxy: ColliderProxy | null } = { proxy: proxy ?? null };
        // Pad-box fallback: with pivot compensation the mesh is centered on the
        // pad, so the box is too (center = half the scaled height up).
        // Pre-scaled half-extents — scaleProxy does NOT scale a box (scale: 1).
        const hx = ((pvb.max.x - pvb.min.x) / 2) * scale;
        const hy = ((pvb.max.y - pvb.min.y) / 2) * scale;
        const hz = ((pvb.max.z - pvb.min.z) / 2) * scale;
        const colEntry: SiteCollider = {
          key: pl.key, cid, px, py, pz, yaw: pl.yaw, scale, holder: colHolder, fx: pl.wx, fz: pl.wz,
          box: { half: [hx, hy, hz], t: { x: pl.wx, y: wy + hy, z: pl.wz, yaw: pl.yaw, scale: 1 } },
          active: false, suppressed: false,
        };
        build.colliders.push(colEntry);
        // R10: the Rapier body (a BVH for trimeshes, a hull compute for the
        // towers) is DEFERRED to walking range — created now only if the
        // player is already inside the ring, else by the ring tick as they
        // approach. The old path baked every collider during assembly ("a
        // run of trimesh bakes put 8 × multi-ms BVH builds in one frame").
        const tc0 = performance.now();
        const dPlayer = Math.hypot(pl.wx - atlas.localX, pl.wz - atlas.localZ);
        if (colliderRingWant(dPlayer, false)) { this.ringAdd(colEntry); job.collidersNow++; } else job.collidersDeferred++;
        job.tCollider += performance.now() - tc0;
        // Enterable? Register with hide/restore closures over THIS placement's
        // instances + collider (the interior module consumes the registry).
        const bw = (pvb.max.x - pvb.min.x) * scale;
        const bh = (pvb.max.y - pvb.min.y) * scale;
        const bd2 = (pvb.max.z - pvb.min.z) * scale;
        // Collision volume (ground → roof): a SHIP flying into a tower bounces
        // off it, and a HOVERCRAFT on the ground can't drive through a building —
        // both hit the same physics-free solids registry. Props (streetlights,
        // benches) stay pass-through; every real structure ≥ 4 m is solid, so the
        // hovercraft is stopped by hamlet huts as well as the city skyline.
        if (pl.kind !== 'prop' && bh >= 4) {
          addStructureSolid(site.id, { x: pl.wx, z: pl.wz, hx: bw / 2, hz: bd2 / 2, yaw: pl.yaw, base: wy, top: wy + bh });
        }
        if (pl.kind !== 'prop') {
          const el = interiorEligible(pl.key, bw, bh, bd2, ENTERABLE_CIV_KEYS);
          const dg = intDiag[pl.key] ?? (intDiag[pl.key] = { n: 0, ok: 0, w: 0, h: 0, d: 0 });
          dg.n++; if (el) dg.ok++;
          dg.w = Math.round(bw); dg.h = Math.round(bh); dg.d = Math.round(bd2);
        }
        if (pl.kind !== 'prop' && interiorEligible(pl.key, bw, bh, bd2, ENTERABLE_CIV_KEYS)) {
          const entId = `settle:${site.id}:${cid}`;
          const colT = { x: px, y: py, z: pz, yaw: pl.yaw, scale };
          const boxT = { x: pl.wx, y: wy + ((pvb.max.y - pvb.min.y) / 2) * scale, z: pl.wz, yaw: pl.yaw, scale: 1 };
          const boxHalf: [number, number, number] = [((pvb.max.x - pvb.min.x) / 2) * scale, ((pvb.max.y - pvb.min.y) / 2) * scale, ((pvb.max.z - pvb.min.z) / 2) * scale];
          const physics = this.physics;
          const instRef = instancer;
          const self = this;
          const ent: EnterableBuilding = {
            id: entId,
            name: civDisplayName(pl.key) ?? pl.key.replace(/^structure:/, '').replace(/_/g, ' ').replace(/scifi\d*/g, '').trim(),
            key: pl.key,
            x: pl.wx, z: pl.wz, yaw: pl.yaw, groundY: wy,
            w: bw, h: bh, d: bd2,
            hide: () => {
              for (const g of slot.gids) instRef.remove(g);
              slot.gids.length = 0;
              slot.hidden = true;
              colEntry.suppressed = true;
              if (colEntry.active) { physics.removeStaticCollider(cid); colEntry.active = false; }
            },
            restore: () => {
              if (slot.gids.length > 0) return; // already visible
              slot.hidden = false;
              for (const part of slot.parts) {
                const g = instRef.add(part.geometry, part.material, part.matrix);
                if (g >= 0) { slot.gids.push(g); build.instanceIds.push(g); }
              }
              colEntry.suppressed = false;
              void colT; void boxHalf; void boxT;
              self.ringAdd(colEntry); // walls back the moment you step out (the ring keeps them thereafter)
            },
          };
          enterables.set(entId, ent);
          build.enterableIds.push(entId);
          // Terrain flattens under the placed structure (mesh + collider):
          // the slope used to run straight through big towers' interiors.
          job.stamps.push({ x: pl.wx, z: pl.wz, hw: bw / 2 + 0.8, hd: bd2 / 2 + 0.8, yaw: pl.yaw, h: wy + 0.02 });
        }
      }
      if (pl.kind !== 'prop') { // record a debug name-label anchor above each building/landmark
        build.labelData.push({ key: pl.key, x: pl.wx, y: wy + (pvb.max.y - pvb.min.y) * scale + 2.5, z: pl.wz });
        // Dressing anchor: the ACTUAL placed transform (frontages/yards read
        // real scaled footprints, never the plan pads fit-scale outgrew).
        job.dressAnchors.push({
          wx: pl.wx, wz: pl.wz, yaw: pl.yaw,
          w: (pvb.max.x - pvb.min.x) * scale, h: (pvb.max.y - pvb.min.y) * scale, d: (pvb.max.z - pvb.min.z) * scale,
          fn: pl.fn, kind: pl.kind,
        });
      }
      if (pl.kind === 'prop' && STREETLIGHT_KEYS.has(pl.key)) {
        // Lamp HEAD ≈ 85% up the scaled GLB height — where the fixture emits from.
        lampAnchors.push({ x: pl.wx, y: wy + (pvb.max.y - pvb.min.y) * scale * 0.85, z: pl.wz });
      }
    job.ci = ci;
  }

  /** Finalize a fully-assembled settlement (labels, roads group, registries). */
  private finishAssembly(job: AssemblyJob): void {
    const { site, build, lampAnchors } = job;
    // R10 settle report: a first-class number for the perf tour.
    performance.mark(`settle:${site.id}:finish`);
    try {
      const { stages, totalMs } = settleStagesFromMarks(performance.getEntriesByType('mark'), site.id);
      const report: SettleReport = {
        siteId: site.id, tier: site.tier, stages, totalMs,
        placements: job.placements.length, colliders: job.collidersNow, collidersDeferred: job.collidersDeferred,
        overlapMs: +job.tOverlap.toFixed(2), colliderMs: +job.tCollider.toFixed(2), at: Date.now(),
      };
      settleReports.last = report; settleReports.bySite.set(site.id, report);
      for (const stg of ['catalog', 'plan', 'resolve', 'load', 'warm', 'assemble', 'finish']) performance.clearMarks(`settle:${site.id}:${stg}`);
    } catch { /* diagnostics never block the build */ }
    // Real instances now share the warmed batches — the ghosts can go without
    // emptying (and thereby destroying) any batch.
    for (const gid of build.ghostIds) this.instancer?.remove(gid);
    build.ghostIds.length = 0;
    if (this.labelsOn) this.createLabels(build);


    // Roads RE-ENABLED (2026-08-31 wave): the old mismatch came from taking Y
    // off the plan's gradeProfile while the ground was stamped separately —
    // these strips DRAPE on meshHeight (the stamped, visible ground), so
    // ribbon and terrain agree by construction. Metre-scaled V UVs, shared
    // textured materials, pavers on the walking strips.
    if (this.roadMats) {
      const rg = buildRoadGroup(job.plan, site, job.seed, this.roadMats);
      rg.position.set(site.centerX, 0, site.centerZ);
      build.roadGroup.add(rg);
    }
    this.settleRoot.add(build.roadGroup);

    // ── The Unbuilt Town dressing layer: walls/gates/signs/wells/plaza
    // character/frontages/yards/terraces/trees (in-town) + fields/mill/burial
    // ground/quays (hinterland). Procedural, deterministic, instanced.
    // QUEUED off the assembly frame: a city's dressing runs thousands of
    // meshHeight samples — one synchronous burst here would be a stream-in
    // hitch, so tick() drains one dressing job per throttled tick instead.
    // Best-effort — a dressing failure never blocks the settlement itself.
    {
      const npcReach = SETTLEMENT_FOOTPRINT[site.tier] * 0.95;
      // HINTERLAND PROP WAVE (2026-09-07): the `prop:hinter_*` keys the catalogue carries. A mapped part
      // (trees, crops, fences, mill, well, gate, markers, bench, skiff, net rack, trough, barrel, cart,
      // brazier) becomes a PrefabPlacement the builders hand back; dressPrefabs instances the cooked GLB
      // there. A missing key keeps that part procedural (self-heal base).
      const hinterKeys = new Set<string>();
      const hconn = atlas.conn;
      if (hconn) for (const pm of hconn.db.prefabModel.iter()) if (pm.prefabKey.startsWith('prop:hinter_')) hinterKeys.add(pm.prefabKey);
      const registerDressing = (res: { group: THREE.Group; colliders: Array<{ hx: number; hy: number; hz: number; x: number; y: number; z: number; yaw: number }>; solids: Array<{ x: number; z: number; hx: number; hz: number; yaw: number; base: number; top: number }>; seats: Array<{ x: number; z: number; r: number }>; rotors: THREE.Object3D[]; prefabs: PrefabPlacement[] }, tag: string): void => {
        this.settleRoot.add(res.group);
        build.dressingGroups.push(res.group);
        if (res.prefabs.length) void this.dressPrefabs(build, site, res.group, res.prefabs, tag);
        res.colliders.forEach((c, i) => {
          const cid = `dress:${tag}:${site.id}:${i}`;
          this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [c.hx, c.hy, c.hz] }, { x: c.x, y: c.y, z: c.z, yaw: c.yaw, scale: 1 });
          build.colliderIds.push(cid);
        });
        for (const so of res.solids) addStructureSolid(site.id, so);
        res.seats.forEach((st, i) => {
          // NPC keep-outs only where NPCs actually roam (the venue spiral
          // reaches 85% of the reserved footprint) — rim/hinterland pieces
          // skip the per-frame clearance loop entirely.
          if (Math.hypot(st.x - site.centerX, st.z - site.centerZ) > npcReach) return;
          const sid = `dress:${tag}:${site.id}:s${i}`;
          setStructureSeat(sid, { x: st.x, z: st.z, r: st.r, y: 0 });
          build.seatIds.push(sid);
        });
        build.rotors.push(...res.rotors);
        if (this.appRef) void warmThenShow(this.appRef, res.group); // R4: hidden until warm, then revealed
      };
      const guard = (fn: () => void): (() => void) => (): void => {
        if (this.built.get(site.id) !== build) return; // disposed before its turn
        try { fn(); } catch (e) { console.warn('[Settlements] dressing failed for site', site.id, e); }
      };
      this.dressingQueue.push(guard(() => registerDressing(buildSettlementDressing({
        plan: job.plan, site, seed: job.seed,
        anchors: job.dressAnchors, lampAnchors, stamps: job.stamps, prefabs: hinterKeys,
      }), 'town')));
      this.dressingQueue.push(guard(() => registerDressing(buildHinterlandDressing(job.seed, site, hinterKeys), 'land')));
    }

    if (job.stamps.length > 0) {
      registerBuildingStamps(job.stamps);
      bus.emit('atlas:terrain-restamp', { stamps: job.stamps });
    }
    this.built.set(site.id, build);
    this.assemblyStats.set(site.id, { placed: job.placedN, ladder: job.ladderN, skipped: job.skipN, noMaster: job.noMasterN });
    this.building.delete(site.id);
    this.buildStage.delete(site.id);
    registerLamps(`settle:${site.id}`, lampAnchors); // lamps light up at night
    // Roads-group props/labels pipelines — TARGETED (whole-scene sweeps were
    // the dominant warm cost; pool batches are hook-warmed at birth already).
    if (this.appRef) void warmThenShow(this.appRef, build.roadGroup); // R4
  }

  // ── Living spaceport ships (shared-clock liftoff/landing loop) ─────────────

  /** Park the animated ship clone on its pad and arm the grounded collider. */
  private placeShip(site: SettlementSite, master: THREE.Object3D, px: number, py: number, pz: number, yaw: number, scale: number): void {
    this.removeShip(site.id); // re-assembly across a LOD boundary re-places it
    const group = master.clone();
    group.position.set(px, py, pz);
    group.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    group.scale.setScalar(scale);
    this.settleRoot.add(group);
    const bb = this.bboxFor(SKY_SHIP_PREFAB, master);
    const ship = {
      group, x: px, homeY: py, z: pz, yaw, scale,
      // Per-city phase offset — the whole world's ships must not move in
      // lockstep; the SHARED wall clock keeps every client in agreement.
      offset: h01(site.siteSeed, 424243) * SHIP_CYCLE_S,
      colliderOn: false,
      proxy: this.proxies.get(SKY_SHIP_PREFAB) ?? null,
      half: [
        (bb.max.x - bb.min.x) / 2 * scale,
        (bb.max.y - bb.min.y) / 2 * scale,
        (bb.max.z - bb.min.z) / 2 * scale,
      ] as [number, number, number],
    };
    this.ships.set(site.id, ship);
    // Publish the hull's MEASURED frame for the boardable interior.
    const hullBottomY = py + bb.min.y * scale + SHIP_ALT_M;
    const hullTopY = py + bb.max.y * scale + SHIP_ALT_M;
    // The interior lives INSIDE the hull: its ship-local origin sits at 22%
    // of hull height, so the concourse, stack, gallery and prow deck all fall
    // within the mesh (the prow's last 60 m alone cantilever past the nose).
    const deckBaseY = hullBottomY + (hullTopY - hullBottomY) * 0.22;
    // The frame's XZ is the hull's CENTER (== the pad the placement aimed at),
    // never the group origin — the model pivot sits kilometres away (see
    // hullCenter in lib/skyship_registry.ts; site 10 measured 3,848 m).
    const center = hullCenter(px, pz, yaw, (bb.min.x + bb.max.x) / 2, (bb.min.z + bb.max.z) / 2, scale);
    registerSkyShip(site.id, {
      x: center.x, z: center.z, yaw, hullBottomY, hullTopY, deckBaseY,
      longAxisX: (bb.max.x - bb.min.x) > (bb.max.z - bb.min.z),
    });
    this.updateShips(); // seat the correct phase immediately (no ground pop)
  }

  private removeShip(siteId: number): void {
    const ship = this.ships.get(siteId);
    if (!ship) return;
    unregisterSkyShip(siteId);
    this.settleRoot.remove(ship.group);
    if (ship.colliderOn) this.physics.removeStaticCollider(`ship:${siteId}`);
    this.ships.delete(siteId);
  }

  /** Per-frame (RenderSub/37): the SKY-SHIP law (mega-ship wave 2026-08-31).
   *  The old grounded→ascent→away→descent cycle parked a >1700 m-raw hull ON
   *  the pad four minutes in ten — sprawled straight through the skyline
   *  (the Ostorch report). The ship now HOLDS STATION aloft, permanently:
   *  a silhouette over the city, never a squatter in it. No ground collider
   *  ever exists (nothing to collide with at 760 m — the walkable interior
   *  carries its own boxes via SkyShipModule); the pad below hosts the
   *  boarding hall instead. */
  private updateShips(): void {
    if (this.ships.size === 0) return;
    for (const [siteId, ship] of this.ships) {
      ship.group.position.y = ship.homeY + SHIP_ALT_M;
      ship.group.visible = true;
      if (ship.colliderOn) {
        // Defensive: a pre-wave grounded collider from an old build path.
        this.physics.removeStaticCollider(`ship:${siteId}`);
        ship.colliderOn = false;
      }
    }
  }

  /** Built settlement structures near a world point (the local map's building
   *  layer — label anchors double as per-building positions; props excluded). */
  structuresNear(x: number, z: number, r: number): Array<{ key: string; x: number; z: number }> {
    const out: Array<{ key: string; x: number; z: number }> = [];
    const r2 = r * r;
    for (const build of this.built.values()) {
      for (const d of build.labelData) {
        const dx = d.x - x, dz = d.z - z;
        if (dx * dx + dz * dz <= r2) out.push({ key: d.key, x: d.x, z: d.z });
      }
    }
    return out;
  }

  /** Natural (scale-1) bounding box of a prefab, cached by key. */
  private bboxFor(key: string, master: THREE.Object3D): THREE.Box3 {
    let b = this.bboxCache.get(key);
    if (!b) {
      // STRAY-PROOF BOUNDS (sky-ship wave): the cooked civa_ship carries
      // far-flung stray vertices (>1700 m raw) — setFromObject returns a box
      // that has nothing to do with the visible hull, and pivot compensation
      // then seats the garbage (at ×8 that displaced the hull hundreds of
      // metres back INTO the city). A 1st/99th-percentile box over sampled
      // vertices ignores the strays and measures the hull people see.
      b = key === SKY_SHIP_PREFAB ? robustBox(master) : new THREE.Box3().setFromObject(master);
      this.bboxCache.set(key, b);
    }
    return b;
  }

  /** Tear down one settlement: remove instances, colliders, road geometry, asset refs. */
  private disposeSettlement(siteId: number): void {
    this.removeShip(siteId);
    const build = this.built.get(siteId);
    if (!build) return;
    unregisterLamps(`settle:${siteId}`);
    for (const eid of build.enterableIds) enterables.delete(eid);
    // instanceIds may hold ids a screen-space swap already retired — dedupe and
    // add the live per-building ids + the pending retirements.
    const ids = new Set<number>(build.instanceIds);
    for (const rec of build.lodBuildings) for (const g of rec.gids) ids.add(g);
    for (const f of build.fades) { for (const g of f.inGids) ids.add(g); for (const g of f.outGids) ids.add(g); }
    for (const id of ids) this.instancer?.remove(id);
    for (const lm of build.lodMasters.values()) lm.release(); // refinement-loaded masters (the build's own are in releases)
    build.lodMasters.clear();
    build.lodBuildings.length = 0;
    build.fades.length = 0;
    for (const gid of build.ghostIds) this.instancer?.remove(gid); // defensive — finishAssembly already cleared them
    for (const cid of build.colliderIds) this.physics.removeStaticCollider(cid);
    for (const c of build.colliders) this.ringRemove(c);
    build.colliders.length = 0;
    for (const sid of build.seatIds) clearStructureSeat(sid);
    for (const g of build.dressingGroups) {
      this.settleRoot.remove(g);
      // Geometries are owned per settlement; the material set is SHARED and
      // never disposed (the text-plank materials own tiny canvas textures —
      // cached module-wide, also never disposed).
      // Generated-prop instances (userData.sharedAsset) borrow the asset cache's geometry — never disposed here.
      g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && !m.userData.sharedAsset) disposeLater(m.geometry); });
    }
    build.dressingGroups.length = 0;
    build.rotors.length = 0;
    this.removeLabels(build);
    this.settleRoot.remove(build.roadGroup);
    build.roadGroup.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) disposeLater(m.geometry); }); // deferred (warp-teardown law)
    for (const rel of build.releases) rel(); // release AFTER instances are gone
    clearStructureSolids(siteId); // drop this settlement's ship-collision solids
    this.built.delete(siteId);
    this.builtLod.delete(siteId);
  }

  // ── Debug: GLB-name labels above each building (toggled from the admin debug panel) ──

  /** Show/hide the prefab-key labels above every built settlement's buildings. */
  setMeshLabels(on: boolean): void {
    if (on === this.labelsOn) return;
    this.labelsOn = on;
    for (const build of this.built.values()) {
      if (on) this.createLabels(build);
      else this.removeLabels(build);
    }
  }

  private createLabels(build: SiteBuild): void {
    if (build.labelSprites.length > 0) return; // already shown
    for (const d of build.labelData) {
      const mat = this.labelMaterial(d.key);
      const sprite = new THREE.Sprite(mat);
      sprite.position.set(d.x, d.y, d.z);
      const img = mat.map?.image as { width: number; height: number } | undefined;
      const aspect = img ? img.width / img.height : 6;
      sprite.scale.set(aspect * LABEL_HEIGHT_M, LABEL_HEIGHT_M, 1);
      sprite.renderOrder = 999;
      this.settleRoot.add(sprite);
      build.labelSprites.push(sprite);
    }
  }

  private removeLabels(build: SiteBuild): void {
    for (const s of build.labelSprites) this.settleRoot.remove(s);
    build.labelSprites.length = 0;
  }

  /** A cached, billboarded text label material for a prefab key (drawn on a canvas). */
  private labelMaterial(key: string): THREE.SpriteMaterial {
    const cached = this.labelMats.get(key);
    if (cached) return cached;
    const text = key.replace(/^structure:|^prop:/, '');
    const font = 30;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = `${font}px monospace`;
    canvas.width = Math.ceil(ctx.measureText(text).width) + 24;
    canvas.height = font + 18;
    ctx.font = `${font}px monospace`; // resizing the canvas resets the 2D context
    ctx.fillStyle = 'rgba(8,10,16,0.78)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#9cf';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 12, canvas.height / 2);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
    this.labelMats.set(key, mat);
    return mat;
  }

  private resetBuilds(): void {
    this.dressingQueue.length = 0; // pending dressing jobs die with their builds
    for (const id of [...this.built.keys()]) this.disposeSettlement(id);
    for (const id of [...this.warmHeld.keys()]) this.releaseWarm(id);
  }

  dispose(): void {
    this.resetBuilds();
    this.scene?.remove(this.settleRoot);
    this.instancer?.dispose();
    this.roadMat?.dispose();
    this.sidewalkMat?.dispose();
    for (const m of this.labelMats.values()) { m.map?.dispose(); m.dispose(); }
    this.labelMats.clear();
    this.planWorker?.terminate();
    this.planWorker = null;
    for (const w of this.planWaiters.values()) w.resolve(null);
    this.planWaiters.clear();
  }
}

/**
 * Every renderable mesh under a GLB, each with its transform relative to the
 * GLB root. Memoized per master (WeakMap): a shared prefab master is INVARIANT
 * once loaded, but assembly called this once per PLACEMENT — a 40-building
 * city re-ran updateMatrixWorld + traverse + Matrix4 clones 40× over the same
 * data during the assembly window.
 */
const collectCache = new WeakMap<THREE.Object3D, { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[]>();
function collectMeshes(root: THREE.Object3D): { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[] {
  const cached = collectCache.get(root);
  if (cached) return cached;
  root.updateMatrixWorld(true); // root is identity-rooted → child.matrixWorld is root-local
  const out: { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && m.geometry) {
      out.push({ geometry: m.geometry, material: Array.isArray(m.material) ? m.material[0] : m.material, local: m.matrixWorld.clone() });
    }
  });
  collectCache.set(root, out);
  return out;
}

/** Mean of a polygon's vertices (Float64Array of x,z pairs). */
function polyCentroid(poly: Float64Array): [number, number] {
  let sx = 0; let sz = 0;
  const n = poly.length / 2;
  for (let i = 0; i < poly.length; i += 2) { sx += poly[i]; sz += poly[i + 1]; }
  return [sx / n, sz / n];
}

/** Deterministic [0,1) hash from two integers — picks a prefab variant per placement. */
function h01(a: number, b: number): number {
  let s = (Math.imul(a >>> 0, 0x9e3779b1) ^ Math.imul((b >>> 0) + 1, 0x85ebca77)) >>> 0;
  s = Math.imul(s ^ (s >>> 15), 0x2c1b3c6d) >>> 0;
  s = Math.imul(s ^ (s >>> 13), 0x297a2d39) >>> 0;
  return ((s ^ (s >>> 15)) >>> 0) / 4294967296;
}

/**
 * 1st/99th-percentile bounding box over sampled vertices of every mesh under
 * a master (mesh-space, node transforms composed) — immune to the handful of
 * far-flung stray vertices that make setFromObject useless on the cooked
 * civa_ship. Falls back to the plain box for tiny/degenerate meshes.
 */
function robustBox(root: THREE.Object3D): THREE.Box3 {
  const parts = collectMeshes(root);
  const xs: number[] = [], ys: number[] = [], zs: number[] = [];
  const v = new THREE.Vector3();
  let total = 0;
  for (const p of parts) total += p.geometry.attributes.position?.count ?? 0;
  const stride = Math.max(1, Math.floor(total / 60000));
  for (const p of parts) {
    const pos = p.geometry.attributes.position as THREE.BufferAttribute | undefined;
    if (!pos) continue;
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(p.local);
      xs.push(v.x); ys.push(v.y); zs.push(v.z);
    }
  }
  if (xs.length < 64) return new THREE.Box3().setFromObject(root);
  const pct = (arr: number[], q: number): number => {
    const a = arr.slice().sort((m, n) => m - n);
    return a[Math.max(0, Math.min(a.length - 1, Math.floor(q * (a.length - 1))))];
  };
  return new THREE.Box3(
    new THREE.Vector3(pct(xs, 0.01), pct(ys, 0.01), pct(zs, 0.01)),
    new THREE.Vector3(pct(xs, 0.99), pct(ys, 0.99), pct(zs, 0.99)),
  );
}
